import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Storage from '../utils/storageES.js';
import HomeButton from './HomeButton.jsx';
import GiftModal from './GiftModal.jsx';
import { getCountryCode } from '../utils/countryUtils.js';

const Plans = ({ userData, onNavigate, onCityNameTranslated }) => {
    const { t, i18n } = useTranslation();
    const [settings, setSettings] = useState({});
    const [tripData, setTripData] = useState({});
    const [budgetData, setBudgetData] = useState({
        dailyBudget: 0,
        spentToday: 0,
        totalSpent: 0,
        categories: {
            food: 0,
            transport: 0,
            activities: 0,
            shopping: 0,
            accommodation: 0,
            other: 0
        }
    });
    const [cityData, setCityData] = useState(null);
    const [activeSection, setActiveSection] = useState('inspiration');
    const [scrollToActivity, setScrollToActivity] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState({
        food: true,
        cultural: true,
        monuments: true,
        activities: true,
        locals: true,
        community: true
    });
    const [communitySpots, setCommunitySpots] = useState([]);
    const [giftSpot, setGiftSpot] = useState(null); // Sprint 3 — spot being gifted
    const [loadingCommunitySpots, setLoadingCommunitySpots] = useState(false);
    const [showSchedulingModal, setShowSchedulingModal] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [schedulingData, setSchedulingData] = useState({
        day: 1,
        period: 'morning' // 'morning' or 'afternoon'
    });
    const [unsplashPhoto, setUnsplashPhoto] = useState(null);
    const [unsplashLoading, setUnsplashLoading] = useState(false);
    const [unsplashError, setUnsplashError] = useState(false);
    const [foodPhotos, setFoodPhotos] = useState({});
    const [loadingFoodPhotos, setLoadingFoodPhotos] = useState({});
    const [attractionPhotos, setAttractionPhotos] = useState({});
    const [loadingAttractionPhotos, setLoadingAttractionPhotos] = useState({});
    const [monumentPhotos, setMonumentPhotos] = useState({});
    const [loadingMonumentPhotos, setLoadingMonumentPhotos] = useState({});
    const [activityPhotos, setActivityPhotos] = useState({});
    const [loadingActivityPhotos, setLoadingActivityPhotos] = useState({});
    const [neighborhoodPhotos, setNeighborhoodPhotos] = useState({});
    const [loadingNeighborhoodPhotos, setLoadingNeighborhoodPhotos] = useState({});

    useEffect(() => {
        const userSettings = userData || Storage.getSettings();
        const userTripData = Storage.getTripData();
        console.log('Plans - User settings:', userSettings);
        console.log('Plans - User trip data:', userTripData);
        setSettings(userSettings);
        setTripData(userTripData);

        // Load budget data
        const savedBudget = Storage.get('budgetData', budgetData);
        setBudgetData(savedBudget);

        // Check if we should navigate to a specific tab / scroll to an activity
        if (window.navigationOptions) {
            if (window.navigationOptions.tab) setActiveSection(window.navigationOptions.tab);
            if (window.navigationOptions.activityName) setScrollToActivity(window.navigationOptions.activityName);
            delete window.navigationOptions;
        }

        // Load city data - use current destination
        const city = userSettings.destination || 'Paris';
        const country = userSettings.country || 'France';
        console.log('Plans - DEBUG: Loading city data for city:', city, 'country:', country);
        loadCityData(city, country);
    }, [userData]);

    // Scroll to (and briefly highlight) the activity we navigated in for, once
    // the city data has rendered the cards. Matched by name via data attribute.
    useEffect(() => {
        if (!scrollToActivity || !cityData) return;
        const timer = setTimeout(() => {
            try {
                const safe = (window.CSS && CSS.escape) ? CSS.escape(scrollToActivity) : scrollToActivity;
                const el = document.querySelector(`[data-activity-name="${safe}"]`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    const prev = el.style.boxShadow;
                    el.style.transition = 'box-shadow 0.3s ease';
                    el.style.boxShadow = '0 0 0 3px #B8A082, 0 8px 24px rgba(0,0,0,0.18)';
                    setTimeout(() => { el.style.boxShadow = prev; }, 2500);
                }
            } catch { /* ignore selector errors */ }
            setScrollToActivity(null);
        }, 500);
        return () => clearTimeout(timer);
    }, [scrollToActivity, cityData]);

    // Fetch approved community spots for this city
    useEffect(() => {
        const city = userData?.destination || settings.destination;
        const country = userData?.country || settings.country;
        if (!city || !country) return;
        setLoadingCommunitySpots(true);
        fetch(`./api/community-spots.php?country=${encodeURIComponent(country)}&city=${encodeURIComponent(city)}`)
            .then(r => r.json())
            .then(data => { if (data.success) setCommunitySpots(data.spots || []); })
            .catch(() => {})
            .finally(() => setLoadingCommunitySpots(false));
    }, [userData?.destination, userData?.country]);

    // Reload city data when language changes
    useEffect(() => {
        console.log('🔄 LANGUAGE CHANGE DETECTED');
        console.log('   New language:', i18n.language);
        console.log('   Settings destination:', settings.destination);
        console.log('   Settings country:', settings.country);

        if (settings.destination && settings.country) {
            console.log('   ✅ Triggering reload of city data...');
            loadCityData(settings.destination, settings.country);
        } else {
            console.log('   ❌ Missing destination or country - skipping reload');
        }
    }, [i18n.language]);

    // Debug: Log whenever cityData changes
    useEffect(() => {
        if (cityData) {
            console.log('📊 CITY DATA STATE UPDATED:');
            console.log('   City:', cityData.city);
            console.log('   Country:', cityData.country);
            console.log('   Foods (city):', cityData.typicalFoodsCity?.length || 0, cityData.typicalFoodsCity?.[0]?.name);
            console.log('   Foods (country):', cityData.typicalFoodsCountry?.length || 0, cityData.typicalFoodsCountry?.[0]?.name);
            console.log('   Attractions:', cityData.culturalAttractions?.length || 0, cityData.culturalAttractions?.[0]?.name);
            console.log('   Monuments:', cityData.monuments?.length || 0, cityData.monuments?.[0]?.name);
            console.log('   Activities:', cityData.localActivities?.length || 0, cityData.localActivities?.[0]?.name);
        }
    }, [cityData]);

    // Fetch Unsplash photo when cityData is loaded
    useEffect(() => {
        if (cityData && cityData.city && !unsplashPhoto && !unsplashLoading) {
            fetchUnsplashPhoto(cityData.city);
        }
    }, [cityData]);

    // Fetch food photos when city data is loaded
    useEffect(() => {
        if (!cityData) return;

        console.log('🔄 Food photo useEffect triggered - cityData:', cityData);
        console.log('🔄 Current foodPhotos state:', Object.keys(foodPhotos));
        console.log('🔄 Current loadingFoodPhotos state:', Object.keys(loadingFoodPhotos).filter(key => loadingFoodPhotos[key]));

        // Prevent duplicate photo loading for the same city
        const currentCity = cityData.city;
        if (currentCity && window.lastPhotoLoadCity === currentCity) {
            console.log(`🔄 Photos already loaded for ${currentCity}, skipping...`);
            return;
        }
        window.lastPhotoLoadCity = currentCity;

        // Fetch city-specific food photos
        if (cityData.typicalFoodsCity && cityData.typicalFoodsCity.length > 0) {
            console.log('🍽️ Found city foods:', cityData.typicalFoodsCity.map(f => f.name));
            cityData.typicalFoodsCity.forEach((food, index) => {
                if (food.name && !foodPhotos[food.name] && !loadingFoodPhotos[food.name]) {
                    console.log(`🚀 Starting photo fetch for city food #${index + 1}: ${food.name}`);
                    // Add a small delay to prevent API rate limiting
                    setTimeout(() => {
                        fetchFoodPhoto(food.name, food);
                    }, index * 150);
                }
            });
        }

        // Fetch country food photos
        if (cityData.typicalFoodsCountry && cityData.typicalFoodsCountry.length > 0) {
            console.log('🍽️ Found country foods:', cityData.typicalFoodsCountry.map(f => f.name));
            const cityFoodCount = cityData.typicalFoodsCity ? cityData.typicalFoodsCity.length : 0;
            cityData.typicalFoodsCountry.forEach((food, index) => {
                if (food.name && !foodPhotos[food.name] && !loadingFoodPhotos[food.name]) {
                    console.log(`🚀 Starting photo fetch for country food #${index + 1}: ${food.name}`);
                    // Add delay after city foods + extra delay for country foods
                    setTimeout(() => {
                        fetchFoodPhoto(food.name, food);
                    }, (cityFoodCount + index) * 150);
                }
            });
        }

        // Fetch cultural attraction photos
        if (cityData.culturalAttractions && cityData.culturalAttractions.length > 0) {
            console.log('🏛️ Found cultural attractions:', cityData.culturalAttractions.map(a => a.name));
            const foodCount = (cityData.typicalFoodsCity?.length || 0) + (cityData.typicalFoodsCountry?.length || 0);
            cityData.culturalAttractions.forEach((attraction, index) => {
                if (attraction.name && !attractionPhotos[attraction.name] && !loadingAttractionPhotos[attraction.name]) {
                    console.log(`🚀 Starting photo fetch for attraction #${index + 1}: ${attraction.name}`);
                    setTimeout(() => {
                        // Determine item type for better search terms
                        let itemType = 'museum';
                        if (attraction.name.toLowerCase().includes('castle') || attraction.name.toLowerCase().includes('fortress') || attraction.name.toLowerCase().includes('fort')) {
                            itemType = 'castle';
                        }

                        const searchTerms = generateSearchTerms(
                            attraction.name,
                            itemType,
                            settings.country || cityData?.country,
                            cityData.city,
                            attraction.localName
                        );

                        fetchGenericPhoto(
                            attraction.name,
                            'cultural attraction',
                            attractionPhotos,
                            loadingAttractionPhotos,
                            setAttractionPhotos,
                            setLoadingAttractionPhotos,
                            searchTerms,
                            attraction
                        );
                    }, (foodCount + index) * 200);
                }
            });
        }

        // Fetch monument photos
        if (cityData.monuments && cityData.monuments.length > 0) {
            console.log('🏰 Found monuments:', cityData.monuments.map(m => m.name));
            const previousCount = (cityData.typicalFoodsCity?.length || 0) + (cityData.typicalFoodsCountry?.length || 0) + (cityData.culturalAttractions?.length || 0);
            cityData.monuments.forEach((monument, index) => {
                if (monument.name && !monumentPhotos[monument.name] && !loadingMonumentPhotos[monument.name]) {
                    console.log(`🚀 Starting photo fetch for monument #${index + 1}: ${monument.name}`);
                    setTimeout(() => {
                        // Determine item type for better search terms
                        let itemType = 'monument';
                        if (monument.name.toLowerCase().includes('church') || monument.name.toLowerCase().includes('cathedral') || monument.name.toLowerCase().includes('basilica')) {
                            itemType = 'church';
                        } else if (monument.name.toLowerCase().includes('town hall') || monument.name.toLowerCase().includes('city hall') || monument.name.toLowerCase().includes('palace')) {
                            itemType = 'building';
                        }

                        const searchTerms = generateSearchTerms(
                            monument.name,
                            itemType,
                            settings.country || cityData?.country,
                            cityData.city,
                            monument.localName
                        );

                        fetchGenericPhoto(
                            monument.name,
                            'monument',
                            monumentPhotos,
                            loadingMonumentPhotos,
                            setMonumentPhotos,
                            setLoadingMonumentPhotos,
                            searchTerms,
                            monument
                        );
                    }, (previousCount + index) * 200);
                }
            });
        }

        // Fetch activity photos
        if (cityData.localActivities && cityData.localActivities.length > 0) {
            console.log('🎯 Found local activities:', cityData.localActivities.map(a => a.name));
            const previousCount = (cityData.typicalFoodsCity?.length || 0) + (cityData.typicalFoodsCountry?.length || 0) + (cityData.culturalAttractions?.length || 0) + (cityData.monuments?.length || 0);
            cityData.localActivities.forEach((activity, index) => {
                if (activity.name && !activityPhotos[activity.name] && !loadingActivityPhotos[activity.name]) {
                    console.log(`🚀 Starting photo fetch for activity #${index + 1}: ${activity.name}`);
                    setTimeout(() => {
                        // Determine item type for better search terms
                        let itemType = 'plaza';
                        if (activity.name.toLowerCase().includes('beach') || activity.name.toLowerCase().includes('playa')) {
                            itemType = 'beach';
                        } else if (activity.name.toLowerCase().includes('market') || activity.name.toLowerCase().includes('mercado')) {
                            itemType = 'market';
                        } else if (activity.name.toLowerCase().includes('island') || activity.name.toLowerCase().includes('isla')) {
                            itemType = 'island';
                        }

                        const searchTerms = generateSearchTerms(
                            activity.name,
                            itemType,
                            settings.country || cityData?.country,
                            cityData.city,
                            activity.localName
                        );

                        fetchGenericPhoto(
                            activity.name,
                            'activity',
                            activityPhotos,
                            loadingActivityPhotos,
                            setActivityPhotos,
                            setLoadingActivityPhotos,
                            searchTerms,
                            activity
                        );
                    }, (previousCount + index) * 200);
                }
            });
        }

        // Fetch neighborhood photos
        if (cityData.traditionalLocals && cityData.traditionalLocals.length > 0) {
            console.log('🏘️ Found neighborhoods:', cityData.traditionalLocals.map(n => n.name));
            const previousCount = (cityData.typicalFoodsCity?.length || 0) + (cityData.typicalFoodsCountry?.length || 0) + (cityData.culturalAttractions?.length || 0) + (cityData.monuments?.length || 0) + (cityData.localActivities?.length || 0);
            cityData.traditionalLocals.forEach((neighborhood, index) => {
                if (neighborhood.name && !neighborhoodPhotos[neighborhood.name] && !loadingNeighborhoodPhotos[neighborhood.name]) {
                    console.log(`🚀 Starting photo fetch for neighborhood #${index + 1}: ${neighborhood.name}`);
                    setTimeout(() => {
                        const searchTerms = generateSearchTerms(
                            neighborhood.name,
                            'neighborhood',
                            settings.country || cityData?.country,
                            cityData.city,
                            neighborhood.localName
                        );

                        fetchGenericPhoto(
                            neighborhood.name,
                            'neighborhood',
                            neighborhoodPhotos,
                            loadingNeighborhoodPhotos,
                            setNeighborhoodPhotos,
                            setLoadingNeighborhoodPhotos,
                            searchTerms,
                            neighborhood
                        );
                    }, (previousCount + index) * 200);
                }
            });
        }
    }, [cityData, settings.country, settings.destination]);

    // Debug effect to track photo states
    useEffect(() => {
        console.log('📊 Photo state summary:');
        console.log('🍽️ Food photos loaded:', Object.keys(foodPhotos).length);
        console.log('🏛️ Attraction photos loaded:', Object.keys(attractionPhotos).length);
        console.log('🏰 Monument photos loaded:', Object.keys(monumentPhotos).length);
        console.log('🎯 Activity photos loaded:', Object.keys(activityPhotos).length);
        console.log('🏘️ Neighborhood photos loaded:', Object.keys(neighborhoodPhotos).length);
        console.log('⏳ Currently loading:', {
            food: Object.keys(loadingFoodPhotos).filter(key => loadingFoodPhotos[key]).length,
            attractions: Object.keys(loadingAttractionPhotos).filter(key => loadingAttractionPhotos[key]).length,
            monuments: Object.keys(loadingMonumentPhotos).filter(key => loadingMonumentPhotos[key]).length,
            activities: Object.keys(loadingActivityPhotos).filter(key => loadingActivityPhotos[key]).length,
            neighborhoods: Object.keys(loadingNeighborhoodPhotos).filter(key => loadingNeighborhoodPhotos[key]).length
        });
    }, [foodPhotos, attractionPhotos, monumentPhotos, activityPhotos, neighborhoodPhotos, loadingFoodPhotos, loadingAttractionPhotos, loadingMonumentPhotos, loadingActivityPhotos, loadingNeighborhoodPhotos]);

    // Helper function to normalize city names across languages
    const normalizeCityName = (cityName) => {
        // Helper to remove accents and diacritics
        const removeAccents = (str) => {
            return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        };

        const cityMap = {
            // Portuguese cities
            'lisbon': ['lisboa', 'lisbon', 'lisbonne'],
            'porto': ['oporto', 'porto', 'port'],
            // Italian cities
            'rome': ['roma', 'rome'],
            'milan': ['milano', 'milan'],
            'venice': ['venezia', 'venice', 'venecia'],
            'florence': ['firenze', 'florence', 'florencia'],
            'naples': ['napoli', 'naples'],
            'turin': ['torino', 'turin'],
            // Spanish cities
            'seville': ['sevilla', 'seville'],
            'cordoba': ['cordoba'],
            'zaragoza': ['saragossa', 'zaragoza'],
            // French cities
            'marseille': ['marseille', 'marseilles', 'marsella'],
            'nice': ['nice', 'niza'],
            'bordeaux': ['bordeaux', 'burdeos'],
            'strasbourg': ['strasbourg', 'estrasburgo'],
            'brussels': ['brussels', 'bruxelles', 'bruselas'],
            'bruges': ['bruges', 'brugge', 'brujas'],
            'antwerp': ['antwerp', 'antwerpen', 'amberes'],
            'vienna': ['vienna', 'wien', 'viena'],
            'salzburg': ['salzburg', 'salzburgo'],
            // Add more as needed
        };

        // First normalize: lowercase, trim, and remove accents
        const normalizedWithAccents = cityName.toLowerCase().trim();
        const normalized = removeAccents(normalizedWithAccents);

        // Find if this city matches any variant (check both with and without accents)
        for (const [key, variants] of Object.entries(cityMap)) {
            if (variants.some(v => removeAccents(v) === normalized || v === normalizedWithAccents)) {
                return key;
            }
        }

        return normalized;
    };

    const loadCityData = async (city, country) => {
        console.log('========================================');
        console.log('🔍 TRANSLATION DEBUG: Loading city data');
        console.log('========================================');
        console.log('📍 City:', city);
        console.log('🌍 Country:', country);
        console.log('🗣️ Current i18n language:', i18n.language);
        console.log('🗣️ Language split:', i18n.language.split('-'));

        try {
            // First try to load from JSON files
            const countryCode = await getCountryCode(country);
            console.log('🔢 Country code returned:', countryCode);

            if (countryCode) {
                // Determine language folder based on current i18n language
                let langFolder = '';
                if (i18n.language === 'es' || i18n.language === 'es-ES') {
                    langFolder = 'es/';
                    console.log('✅ Spanish detected - using es/ folder');
                } else if (i18n.language === 'fr' || i18n.language === 'fr-FR') {
                    langFolder = 'fr/';
                    console.log('✅ French detected - using fr/ folder');
                } else {
                    langFolder = 'en/';
                    console.log('✅ English/default detected - using en/ folder');
                }

                const url = `./TRIP/${langFolder}${countryCode}.json`;
                console.log('📂 Fetching file from URL:', url);
                console.log('🌐 Full URL would be:', window.location.origin + window.location.pathname + url.substring(2));

                const response = await fetch(url);
                console.log('📥 Fetch response status:', response.status, response.statusText);

                if (response.ok) {
                    const data = await response.json();
                    console.log('✅ JSON loaded successfully!');
                    console.log('📦 Data structure:', {
                        country: data.country,
                        citiesCount: data.cities?.length,
                        firstCity: data.cities?.[0]?.city
                    });

                    // Normalize both the search city and the cities in the data
                    const normalizedSearchCity = normalizeCityName(city);
                    console.log('🔤 Normalized search city:', normalizedSearchCity);
                    console.log('🔍 Available cities in data:', data.cities.map(c => c.city).join(', '));

                    const cityInfo = data.cities.find(c => {
                        const normalizedDataCity = normalizeCityName(c.city);
                        console.log('   Comparing:', normalizedDataCity, '===', normalizedSearchCity, '?', normalizedDataCity === normalizedSearchCity);
                        return normalizedDataCity === normalizedSearchCity;
                    });

                    if (cityInfo) {
                        console.log('✅ City match found!');
                        console.log('📊 City data sample:', {
                            city: cityInfo.city,
                            foodsCity: cityInfo.typicalFoodsCity?.length || 0,
                            foodsCountry: cityInfo.typicalFoodsCountry?.length || 0,
                            attractions: cityInfo.culturalAttractions?.length || 0,
                            monuments: cityInfo.monuments?.length || 0,
                            activities: cityInfo.localActivities?.length || 0,
                            firstFood: cityInfo.typicalFoodsCity?.[0]?.name,
                            firstAttraction: cityInfo.culturalAttractions?.[0]?.name
                        });
                        setCityData(cityInfo);
                        // Emit translated city name to parent component
                        if (onCityNameTranslated && cityInfo.city) {
                            onCityNameTranslated(cityInfo.city);
                            console.log('📤 Emitted translated city name:', cityInfo.city);
                        }
                        console.log('========================================');
                        return;
                    } else {
                        console.log('❌ No city match found in JSON data');
                    }
                } else {
                    console.log('❌ Fetch failed with status:', response.status);
                }
            } else {
                console.log('❌ No country code returned');
            }

            // Enhanced fallback: Use WebSearch to gather city information
            console.log('⚠️ Falling back to WebSearch');
            console.log('========================================');
            await loadCityDataFromWebSearch(city, country);
            
        } catch (error) {
            console.error('Plans - Error loading city data:', error);
            // Ultimate fallback with basic structure
            setCityData({
                city: city,
                country: country,
                briefDescription: `${city} is a beautiful destination in ${country}.`,
                climate: "Climate information not available",
                currency: "Local currency",
                timezone: { standardTime: "Local timezone" },
                languagesSpoken: { officialLanguage: "Local language" },
                typicalFoodsCity: [],
                typicalFoodsCountry: [],
                culturalAttractions: [],
                monuments: [],
                localActivities: [],
                traditionalLocals: [],
                isWebSearchData: true
            });
        }
    };

    const loadCityDataFromWebSearch = async (city, country) => {
        console.log('Plans - Using WebSearch for:', city, country);
        
        try {
            // Create initial structure while we search
            const searchingData = {
                city: city,
                country: country,
                briefDescription: `Loading information about ${city}...`,
                climate: "Loading climate information...",
                currency: "Loading currency information...",
                timezone: { standardTime: "Loading timezone..." },
                languagesSpoken: { officialLanguage: "Loading language information..." },
                typicalFoodsCity: [],
                culturalAttractions: [],
                monuments: [],
                localActivities: [],
                traditionalLocals: [],
                isWebSearchData: true,
                loading: true
            };
            setCityData(searchingData);

            // Parallel search for different types of information
            const searchPromises = [
                // Basic city information
                fetch('/api/websearch', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        query: `${city} ${country} travel guide overview climate currency timezone language`,
                        prompt: `Extract basic travel information about ${city}, ${country}. Return a brief description, climate, currency, timezone, and official language in a structured format.`
                    })
                }).catch(() => null),
                
                // Food and cuisine
                fetch('/api/websearch', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        query: `${city} ${country} local food traditional dishes cuisine restaurants`,
                        prompt: `Find the top 5 traditional foods and dishes specific to ${city}, ${country}. Include name and description for each dish.`
                    })
                }).catch(() => null),
                
                // Cultural attractions and monuments
                fetch('/api/websearch', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        query: `${city} ${country} cultural attractions museums monuments landmarks`,
                        prompt: `List the top 5 cultural attractions, museums, and monuments in ${city}, ${country}. Include name and brief description for each.`
                    })
                }).catch(() => null),
                
                // Activities and neighborhoods
                fetch('/api/websearch', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        query: `${city} ${country} activities things to do neighborhoods districts`,
                        prompt: `Find the top 5 activities and traditional neighborhoods/districts in ${city}, ${country}. Include name and description for each.`
                    })
                }).catch(() => null)
            ];

            const results = await Promise.allSettled(searchPromises);
            
            // Process search results and build comprehensive city data
            const enhancedCityData = await processCitySearchResults(city, country, results);
            setCityData(enhancedCityData);
            
        } catch (error) {
            console.error('Plans - WebSearch error:', error);
            // Fallback to basic structure if web search fails
            setCityData({
                city: city,
                country: country,
                briefDescription: `${city} is a destination in ${country}. Detailed information will be available soon.`,
                climate: "Climate information not available",
                currency: "Local currency",
                timezone: { standardTime: "Local timezone" },
                languagesSpoken: { officialLanguage: "Local language" },
                typicalFoodsCity: [{
                    name: "Local Cuisine",
                    description: `Explore the traditional flavors of ${city}`
                }],
                culturalAttractions: [{
                    name: "Historic Center",
                    description: `Visit the historic center of ${city}`
                }],
                monuments: [{
                    name: "City Landmarks", 
                    description: `Discover the landmarks of ${city}`
                }],
                localActivities: [{
                    name: "City Walking Tour",
                    description: `Take a walking tour around ${city}`
                }],
                traditionalLocals: [{
                    name: "Old Town",
                    description: `Explore the traditional neighborhoods of ${city}`
                }],
                isWebSearchData: true,
                webSearchFailed: true
            });
        }
    };

    const processCitySearchResults = async (city, country, results) => {
        console.log('Processing search results:', results);
        
        // Initialize the city data structure
        const cityData = {
            city: city,
            country: country,
            isWebSearchData: true,
            searchEnhanced: true,
            typicalFoodsCity: [],
            culturalAttractions: [],
            monuments: [],
            localActivities: [],
            traditionalLocals: []
        };

        // Process each search result
        results.forEach((result, index) => {
            if (result.status === 'fulfilled' && result.value && result.value.ok) {
                result.value.json().then(data => {
                    if (data.success && data.results) {
                        const searchResults = data.results;
                        
                        switch (index) {
                            case 0: // Basic city information
                                if (searchResults.briefDescription) cityData.briefDescription = searchResults.briefDescription;
                                if (searchResults.climate) cityData.climate = searchResults.climate;
                                if (searchResults.currency) cityData.currency = searchResults.currency;
                                if (searchResults.timezone) cityData.timezone = searchResults.timezone;
                                if (searchResults.languagesSpoken) cityData.languagesSpoken = searchResults.languagesSpoken;
                                break;
                                
                            case 1: // Food and cuisine
                                if (searchResults.dishes && Array.isArray(searchResults.dishes)) {
                                    cityData.typicalFoodsCity = searchResults.dishes;
                                }
                                break;
                                
                            case 2: // Cultural attractions and monuments
                                if (searchResults.attractions && Array.isArray(searchResults.attractions)) {
                                    // Split into cultural attractions and monuments
                                    cityData.culturalAttractions = searchResults.attractions.slice(0, 3);
                                    cityData.monuments = searchResults.attractions.slice(3, 5);
                                }
                                break;
                                
                            case 3: // Activities and neighborhoods
                                if (searchResults.activities && Array.isArray(searchResults.activities)) {
                                    cityData.localActivities = searchResults.activities;
                                }
                                if (searchResults.neighborhoods && Array.isArray(searchResults.neighborhoods)) {
                                    cityData.traditionalLocals = searchResults.neighborhoods;
                                }
                                break;
                        }
                    }
                }).catch(err => console.log('Error processing search result:', err));
            }
        });
        
        // Return enhanced city data with fallbacks for any missing information
        return {
            city: city,
            country: country,
            briefDescription: cityData.briefDescription || `${city} is a fascinating destination in ${country} with rich cultural heritage and local attractions. This information was gathered from web sources.`,
            climate: cityData.climate || `${city} experiences a regional climate typical of ${country}.`,
            currency: cityData.currency || (country === 'France' ? 'Euro (€)' : 'Local currency'),
            timezone: cityData.timezone || { 
                standardTime: country === 'France' ? 'CET (UTC+1)' : 'Local timezone',
                daylightSavingTime: country === 'France' ? 'CEST (UTC+2)' : undefined
            },
            languagesSpoken: cityData.languagesSpoken || { 
                officialLanguage: country === 'France' ? 'French' : 'Local language',
                note: 'English is commonly understood in tourist areas.'
            },
            typicalFoodsCity: cityData.typicalFoodsCity.length > 0 ? cityData.typicalFoodsCity : [
                {
                    name: `${city} Specialties`,
                    description: `Local culinary specialties unique to ${city}`
                },
                {
                    name: "Regional Cuisine",
                    description: `Traditional dishes from the ${city} region`
                }
            ],
            culturalAttractions: cityData.culturalAttractions.length > 0 ? cityData.culturalAttractions : [
                {
                    name: `${city} History Museum`,
                    description: `Museum showcasing the rich historical heritage of ${city}`
                },
                {
                    name: "Cultural Center",
                    description: `Local cultural center featuring exhibitions and events`
                }
            ],
            monuments: cityData.monuments.length > 0 ? cityData.monuments : [
                {
                    name: `${city} Cathedral/Church`,
                    description: `Main religious and architectural monument of ${city}`
                },
                {
                    name: "City Square",
                    description: `Central square and civic monuments in ${city}`
                }
            ],
            localActivities: cityData.localActivities.length > 0 ? cityData.localActivities : [
                {
                    name: "Walking Food Tour",
                    description: `Explore local restaurants and food markets in ${city}`
                },
                {
                    name: "Historic District Walk",
                    description: `Self-guided walk through the oldest parts of ${city}`
                }
            ],
            traditionalLocals: cityData.traditionalLocals.length > 0 ? cityData.traditionalLocals : [
                {
                    name: "Historic Quarter",
                    description: `Traditional neighborhood showcasing ${city}'s heritage`
                },
                {
                    name: "Artisan District", 
                    description: `Area known for local crafts and traditional trades`
                }
            ],
            isWebSearchData: true,
            searchEnhanced: true
        };
    };

    // Fetch Unsplash photo for the city
    // IMPORTANT: Unsplash API requires proper attribution as per their guidelines:
    // - Photographer name must be displayed
    // - "on Unsplash" text must be included
    // - Link to photographer's Unsplash profile (if implementing clicks)
    const fetchUnsplashPhoto = async (cityName) => {
        if (!cityName) return;

        console.log(`🌍 Loading photo for city: ${cityName}`);
        setUnsplashLoading(true);
        setUnsplashError(false);

        try {
            // Use Pexels API for real city sunset photos
            console.log(`🌅 Searching Pexels for ${cityName} sunset photos...`);

            // Use the country from cityData if available, or from settings
            const country = cityData?.country || settings?.country || '';
            const query = `${cityName} cityscape `;
            const pexelsUrl = `https://api.pexels.com/v1/search?query=${encodeURIComponent(query)}&per_page=5&orientation=landscape`;

            console.log(`🔍 Searching Pexels with query: "${query}"`);

            const response = await fetch(pexelsUrl, {
                headers: {
                    'Authorization': 'dXyoDR42oJjkIbLJv121mvP1YF9mNlxcMZqECbZkYn61niPALBsrYGgY',
                    'Accept': 'application/json',
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.photos && data.photos.length > 0) {
                    // Try to find a photo that mentions the city in alt text
                    let selectedPhoto = data.photos.find(photo =>
                        photo.alt && photo.alt.toLowerCase().includes(cityName.toLowerCase())
                    );

                    // If no city-specific photo found, use the first one
                    if (!selectedPhoto) {
                        selectedPhoto = data.photos[0];
                    }

                    console.log(`✅ Got Pexels photo for ${cityName} by:`, selectedPhoto.photographer);
                    console.log(`📝 Photo alt text:`, selectedPhoto.alt);

                    setUnsplashPhoto({
                        urls: { regular: selectedPhoto.src.medium },
                        alt_description: selectedPhoto.alt || `${cityName} sunset view`,
                        user: { name: selectedPhoto.photographer },
                        photographer_url: selectedPhoto.photographer_url,
                        fallback: false,
                        source: 'pexels'
                    });
                    return;
                }
            }

            console.log('Pexels API did not return results, using fallback');
            throw new Error('No Pexels results');

        } catch (error) {
            console.error('Error fetching Unsplash photo:', error);
            setUnsplashError(true);
            // Set a placeholder photo
            setUnsplashPhoto({
                urls: { regular: `https://picsum.photos/400/200?random=${Math.floor(Math.random() * 1000)}` },
                alt_description: `Beautiful view of ${cityName}`,
                user: { name: 'Placeholder Photo' },
                fallback: true
            });
        } finally {
            setUnsplashLoading(false);
        }
    };


    // Helper function to search for JPG/PNG photos on a Wikipedia page
    const findPhotoInWikipediaPage = async (title, lang) => {
        try {
            console.log(`🔍 Searching for photos in Wikipedia page: ${title}`);

            // Get all images on the page
            const imagesUrl = `https://${lang}.wikipedia.org/w/api.php?action=query&format=json&origin=*&prop=images&titles=${encodeURIComponent(title)}`;
            const imagesResponse = await fetch(imagesUrl);
            if (!imagesResponse.ok) return null;

            const imagesData = await imagesResponse.json();
            const pages = imagesData.query?.pages;
            if (!pages) return null;

            const pageId = Object.keys(pages)[0];
            const page = pages[pageId];
            if (!page.images) return null;

            // Find JPG/PNG images
            const photoImages = page.images.filter(img => {
                const name = img.title.toLowerCase();
                return (name.includes('.jpg') || name.includes('.jpeg') || name.includes('.png')) &&
                       !name.includes('commons-logo') && !name.includes('wikidata-logo');
            });

            if (photoImages.length === 0) return null;

            // Get details for the first photo
            const firstPhoto = photoImages[0];
            const imageInfoUrl = `https://${lang}.wikipedia.org/w/api.php?action=query&format=json&origin=*&prop=imageinfo&titles=${encodeURIComponent(firstPhoto.title)}&iiprop=url&iiurlwidth=400`;

            const imageInfoResponse = await fetch(imageInfoUrl);
            if (!imageInfoResponse.ok) return null;

            const imageInfoData = await imageInfoResponse.json();
            const imagePages = imageInfoData.query?.pages;
            if (!imagePages) return null;

            const imagePageId = Object.keys(imagePages)[0];
            const imagePage = imagePages[imagePageId];
            const imageInfo = imagePage.imageinfo?.[0];

            if (imageInfo?.thumburl) {
                console.log(`✅ Found photo in page: ${imageInfo.thumburl}`);
                return {
                    thumbnail: imageInfo.thumburl,
                    original: imageInfo.url || imageInfo.thumburl
                };
            }

            return null;
        } catch (error) {
            console.error(`Error searching for photos in Wikipedia page:`, error);
            return null;
        }
    };

    // Helper function to check if URL is a valid photo (not SVG or other non-photo formats)
    const isValidPhotoUrl = (url) => {
        if (!url) return false;

        const urlLower = url.toLowerCase();

        // Exclude SVG files completely
        if (urlLower.includes('.svg')) {
            console.log(`🚫 Skipping SVG file: ${url}`);
            return false;
        }

        // Exclude other non-photo formats
        const excludedFormats = ['.svg', '.gif', '.webm', '.mp4', '.pdf', '.doc'];
        if (excludedFormats.some(format => urlLower.includes(format))) {
            console.log(`🚫 Skipping non-photo format: ${url}`);
            return false;
        }

        // Accept common photo formats
        const photoFormats = ['.jpg', '.jpeg', '.png', '.webp', '.bmp', '.tiff'];
        const hasPhotoFormat = photoFormats.some(format => urlLower.includes(format));

        if (hasPhotoFormat) {
            console.log(`✅ Valid photo format detected: ${url}`);
            return true;
        }

        // If no specific format detected, but it's from a trusted source, allow it
        // (Some Wikipedia URLs don't have extensions but are valid photos)
        if (urlLower.includes('upload.wikimedia.org') || urlLower.includes('commons.wikimedia.org')) {
            console.log(`📸 Wikimedia photo (no extension): ${url}`);
            return true;
        }

        console.log(`❓ Unknown format, allowing: ${url}`);
        return true;
    };

    // Get the appropriate Wikipedia language code based on country/city
    const getWikipediaLanguage = (country, city) => {
        const languageMap = {
            'spain': 'es',
            'france': 'fr',
            'italy': 'it',
            'portugal': 'pt',
            'netherlands': 'nl',
            'belgium': 'nl', // Primary language, though French could also be used
            'switzerland': 'de', // Could also be fr or it depending on region
            'austria': 'de',
            'germany': 'de',
            'united kingdom': 'en',
            'uk': 'en',
            'england': 'en',
            'czech republic': 'cs',
            'croatia': 'hr',
            'denmark': 'da',
            'finland': 'fi',
            'ireland': 'en',
            'norway': 'no',
            'sweden': 'sv',
            'poland': 'pl',
            'hungary': 'hu',
            'romania': 'ro',
            'bulgaria': 'bg',
            'greece': 'el',
            'turkey': 'tr',
            'russia': 'ru'
        };

        return languageMap[country?.toLowerCase()] || 'en';
    };

    // Get localized architectural/place terms for better Wikipedia searches
    const getLocalizedTerms = (country, itemType) => {
        const termMaps = {
            'spain': {
                castle: ['castillo', 'castell', 'fortaleza', 'alcázar'],
                museum: ['museo', 'museu'],
                church: ['iglesia', 'basílica', 'catedral', 'concatedral'],
                building: ['edificio', 'palacio', 'casa'],
                monument: ['monumento', 'memorial'],
                beach: ['playa', 'platja'],
                market: ['mercado', 'mercat'],
                plaza: ['plaza', 'plaça'],
                neighborhood: ['barrio', 'distrito'],
                island: ['isla', 'illa']
            },
            'france': {
                castle: ['château', 'fort', 'forteresse'],
                museum: ['musée'],
                church: ['église', 'cathédrale', 'basilique'],
                building: ['bâtiment', 'palais', 'maison'],
                monument: ['monument', 'mémorial'],
                beach: ['plage'],
                market: ['marché'],
                plaza: ['place', 'square'],
                neighborhood: ['quartier', 'arrondissement'],
                island: ['île']
            },
            'italy': {
                castle: ['castello', 'forte', 'fortezza'],
                museum: ['museo'],
                church: ['chiesa', 'cattedrale', 'basilica'],
                building: ['edificio', 'palazzo', 'casa'],
                monument: ['monumento', 'memoriale'],
                beach: ['spiaggia'],
                market: ['mercato'],
                plaza: ['piazza', 'square'],
                neighborhood: ['quartiere', 'zona'],
                island: ['isola']
            },
            'germany': {
                castle: ['schloss', 'burg', 'festung'],
                museum: ['museum'],
                church: ['kirche', 'dom', 'basilika'],
                building: ['gebäude', 'palast', 'haus'],
                monument: ['denkmal', 'monument'],
                beach: ['strand'],
                market: ['markt'],
                plaza: ['platz', 'square'],
                neighborhood: ['stadtteil', 'viertel'],
                island: ['insel']
            }
        };

        return termMaps[country?.toLowerCase()] || {};
    };

    // Generate dynamic search terms for any item in any city/country
    const generateSearchTerms = (itemName, itemType, country, city, localName = null) => {
        const searchName = localName || itemName;
        const localTerms = getLocalizedTerms(country, itemType);
        const cityName = city || '';

        // Base terms with city context
        const baseTerms = [
            `${searchName} ${cityName}`,
            `${searchName}`,
            searchName
        ];

        // Add localized architectural terms
        const typeTerms = [];
        if (localTerms[itemType]) {
            localTerms[itemType].forEach(localTerm => {
                typeTerms.push(`${searchName} ${localTerm} ${cityName}`);
                typeTerms.push(`${searchName} ${localTerm}`);
            });
        }

        // Add English fallback terms
        const englishTerms = [
            `${searchName} ${itemType} ${cityName}`,
            `${searchName} ${itemType}`
        ];

        // Combine all terms, removing duplicates
        const allTerms = [...baseTerms, ...typeTerms, ...englishTerms];
        return [...new Set(allTerms.filter(term => term.trim().length > 0))];
    };

    // Enhanced food photo fetching with international support
    const fetchFoodPhoto = async (foodName, foodItem = null) => {
        if (!foodName || foodPhotos[foodName] || loadingFoodPhotos[foodName]) return;

        setLoadingFoodPhotos(prev => ({ ...prev, [foodName]: true }));
        console.log(`🍽️ Loading photo for food: ${foodName}`);

        try {
            // Get the appropriate Wikipedia language
            const wikiLang = getWikipediaLanguage(settings.country || cityData?.country, settings.destination || cityData?.city);
            console.log(`🌍 Using ${wikiLang} Wikipedia for ${settings.country}`);

            // Use localName if available, otherwise use the regular name
            const searchName = foodItem?.localName || foodName;
            console.log(`🔤 Using search name: "${searchName}" ${foodItem?.localName ? '(local name)' : '(English name)'}`);

            // Clean the food name and create variations
            const cleanedFoodName = searchName.replace(/\s*\([^)]*\)/g, '').trim();

            // Create search variations
            const variations = [
                cleanedFoodName,
                cleanedFoodName.toLowerCase(),
                cleanedFoodName.charAt(0).toUpperCase() + cleanedFoodName.slice(1).toLowerCase(),
                cleanedFoodName.split(' ').map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                ).join(' ')
            ];

            // Remove duplicates
            const uniqueVariations = [...new Set(variations)];
            console.log(`🔍 Trying Wikipedia variations for "${cleanedFoodName}":`, uniqueVariations);

            // Try local Wikipedia first, then English as fallback
            const wikipediaLanguages = wikiLang !== 'en' ? [wikiLang, 'en'] : ['en'];

            for (const lang of wikipediaLanguages) {
                console.log(`🔍 Searching ${lang}.wikipedia.org...`);

                for (const variation of uniqueVariations) {
                    const wikiUrl = `https://${lang}.wikipedia.org/w/api.php?action=query&format=json&origin=*&prop=pageimages|pageterms&titles=${encodeURIComponent(variation)}&pithumbsize=400&piprop=thumbnail|name|original`;
                    console.log(`🌐 FOOD: Testing URL: ${wikiUrl}`);

                    try {
                        const response = await fetch(wikiUrl);
                        if (!response.ok) {
                            console.log(`❌ FOOD: HTTP error ${response.status} for "${variation}"`);
                            continue;
                        }

                        const data = await response.json();
                        console.log(`📖 FOOD: ${lang} Wikipedia API response for "${variation}":`, JSON.stringify(data, null, 2));

                        const pages = data.query?.pages;
                        if (!pages) continue;

                        const pageId = Object.keys(pages)[0];
                        const page = pages[pageId];

                        // Check for page existence (missing pages have negative IDs)
                        if (!page || pageId === '-1' || page.missing) {
                            console.log(`❌ ${lang} Wikipedia page not found for ${variation}`);
                            continue;
                        }

                        let imageUrl = null;
                        let originalUrl = null;

                        // Try to get image from different possible properties
                        if (page.thumbnail?.source) {
                            imageUrl = page.thumbnail.source;
                        }
                        if (page.original?.source) {
                            originalUrl = page.original.source;
                        }

                        if (imageUrl && isValidPhotoUrl(imageUrl)) {
                            console.log(`✅ Got ${lang} Wikipedia food photo for ${variation}: ${imageUrl}`);

                            // Create a standardized photo object
                            const photoData = {
                                urls: {
                                    regular: imageUrl,
                                    original: originalUrl || imageUrl
                                },
                                src: {
                                    medium: imageUrl,
                                    original: originalUrl || imageUrl
                                },
                                alt_description: page.title || variation,
                                alt: page.title || variation,
                                photographer: `${lang.toUpperCase()} Wikipedia`,
                                user: { name: `${lang.toUpperCase()} Wikipedia` },
                                description: `${variation} from ${lang.toUpperCase()} Wikipedia`,
                                fallback: false,
                                source: 'wikipedia',
                                language: lang
                            };

                            setFoodPhotos(prev => ({ ...prev, [foodName]: photoData }));
                            return;
                        } else if (imageUrl) {
                            console.log(`🚫 Main image is SVG/invalid for ${variation}: ${imageUrl}`);

                            // Try to find a JPG/PNG photo in the page
                            const pagePhoto = await findPhotoInWikipediaPage(variation, lang);
                            if (pagePhoto) {
                                const photoData = {
                                    urls: {
                                        regular: pagePhoto.thumbnail,
                                        original: pagePhoto.original
                                    },
                                    src: {
                                        medium: pagePhoto.thumbnail,
                                        original: pagePhoto.original
                                    },
                                    alt_description: variation,
                                    alt: variation,
                                    photographer: `${lang.toUpperCase()} Wikipedia`,
                                    user: { name: `${lang.toUpperCase()} Wikipedia` },
                                    description: `${variation} from ${lang.toUpperCase()} Wikipedia`,
                                    fallback: false,
                                    source: 'wikipedia',
                                    language: lang
                                };

                                setFoodPhotos(prev => ({ ...prev, [foodName]: photoData }));
                                return;
                            }
                        } else {
                            console.log(`📄 ${lang} Wikipedia page exists for ${variation} but no image found`);
                        }
                    } catch (err) {
                        console.log(`❌ Error fetching from ${lang} Wikipedia for ${variation}:`, err.message);
                        continue;
                    }
                }
            }

            // If Wikipedia search fails, try Wikimedia Commons
            console.log(`📄 No Wikipedia images found, trying Commons fallback`);
            const foodSearchTerms = [
                `${cleanedFoodName} food`,
                `${cleanedFoodName} dish`,
                `${cleanedFoodName} cuisine`,
                cleanedFoodName
            ];
            await tryCommonsSearch(cleanedFoodName, foodName, foodSearchTerms, setFoodPhotos);

        } catch (error) {
            console.error(`❌ Error fetching food photo for ${foodName}:`, error);
            console.error(`❌ Error details:`, {
                name: error.name,
                message: error.message,
                stack: error.stack?.split('\n')[0]
            });
        } finally {
            setLoadingFoodPhotos(prev => ({ ...prev, [foodName]: false }));
            console.log(`✅ Finished photo fetch for food: ${foodName}`);
        }
    };

    // Wikimedia Commons search fallback
    const tryCommonsSearch = async (cleanedName, originalName, searchTerms, photoSetter) => {
        try {
            // Try multiple search queries
            const searchQueries = Array.isArray(searchTerms) ? searchTerms : [
                `${cleanedName} food`,
                `${cleanedName} dish`,
                `${cleanedName} cuisine`,
                cleanedName
            ];

            for (const query of searchQueries) {
                const commonsUrl = `https://commons.wikimedia.org/w/api.php?action=query&format=json&origin=*&generator=search&gsrsearch=${encodeURIComponent(query)}&gsrlimit=5&prop=imageinfo&iiprop=url&iiurlwidth=400`;

                console.log(`🔍 Searching Commons for: "${query}"`);

                const response = await fetch(commonsUrl);
                if (!response.ok) continue;

                const data = await response.json();
                console.log(`📖 Commons response for "${query}":`, data);

                const pages = data.query?.pages;
                if (!pages) continue;

                // Get the first image with a valid thumbnail
                for (const pageId of Object.keys(pages)) {
                    const page = pages[pageId];
                    if (page.imageinfo?.[0]?.thumburl) {
                        const imageUrl = page.imageinfo[0].thumburl;
                        const originalUrl = page.imageinfo[0].url;

                        // Check if it's a valid photo format (not SVG)
                        if (!isValidPhotoUrl(imageUrl)) {
                            console.log(`🚫 Skipping Commons non-photo: ${imageUrl}`);
                            continue;
                        }

                        console.log(`✅ Got Commons image for ${cleanedName}: ${imageUrl}`);

                        const photoData = {
                            urls: {
                                regular: imageUrl,
                                original: originalUrl || imageUrl
                            },
                            src: {
                                medium: imageUrl,
                                original: originalUrl || imageUrl
                            },
                            alt_description: page.title || cleanedName,
                            alt: page.title || cleanedName,
                            photographer: 'Wikimedia Commons',
                            user: { name: 'Wikimedia Commons' },
                            description: `${cleanedName} from Wikimedia Commons`,
                            fallback: true,
                            source: 'commons'
                        };

                        photoSetter(prev => ({ ...prev, [originalName]: photoData }));
                        return;
                    }
                }
            }

            console.log(`❌ No Commons images found for ${cleanedName}`);
        } catch (error) {
            console.error(`❌ Commons search failed for ${cleanedName}:`, error);
        }
    };

    // Generic photo fetching function for any type of attraction/place
    const fetchGenericPhoto = async (
        itemName,
        itemType,
        photoState,
        loadingState,
        setPhotoState,
        setLoadingState,
        searchTerms = null,
        itemObject = null
    ) => {
        if (!itemName || photoState[itemName] || loadingState[itemName]) return;

        setLoadingState(prev => ({ ...prev, [itemName]: true }));
        console.log(`🏛️ Loading photo for ${itemType}: ${itemName}`);

        // Debug for Basílica specifically
        if (itemName.includes('Basílica') || itemName.includes('basilica')) {
            console.log(`🔍 BASILICA START - itemName: "${itemName}", itemObject:`, itemObject);
        }

        try {
            // Get the appropriate Wikipedia language
            const wikiLang = getWikipediaLanguage(settings.country || cityData?.country, settings.destination || cityData?.city);
            console.log(`🌍 Using ${wikiLang} Wikipedia for ${settings.country} ${itemType}`);

            // Use localName if available, otherwise use the regular name
            const searchName = itemObject?.localName || itemName;
            console.log(`🔤 Using search name: "${searchName}" ${itemObject?.localName ? '(local name)' : '(English name)'}`);

            // For Wikipedia searches, preserve parentheses that are likely part of official Wikipedia titles
            // Wikipedia uses parentheses for disambiguation (city names, years, types, etc.)
            let cleanedName = searchName;

            // Only remove parentheses if they contain clearly irrelevant content
            // Keep parentheses for: city names, countries, years, types, etc.
            const hasParentheses = /\([^)]+\)/.test(searchName);
            if (hasParentheses) {
                const parenthesesContent = searchName.match(/\(([^)]+)\)/)?.[1];

                // Remove parentheses only if they contain clearly non-Wikipedia content
                // Keep them for geographical, temporal, or type disambiguation
                const shouldRemoveParentheses = parenthesesContent && (
                    // Remove only if it's clearly descriptive text (long phrases, common words)
                    parenthesesContent.length > 50 || // Very long descriptions
                    /\b(descripción|description|información|info|nota|note)\b/i.test(parenthesesContent) ||
                    /^(el|la|los|las|the|a|an)\s+/i.test(parenthesesContent) // Starts with articles
                );

                if (shouldRemoveParentheses) {
                    cleanedName = searchName.replace(/\s*\([^)]*\)/g, '').trim();
                }
                // Otherwise keep the parentheses - they're likely part of the official Wikipedia title
            }

            console.log(`🧹 International search: "${searchName}" → "${cleanedName}"`);

            // Create search variations
            const variations = [
                cleanedName,
                cleanedName.toLowerCase(),
                cleanedName.charAt(0).toUpperCase() + cleanedName.slice(1).toLowerCase(),
                cleanedName.split(' ').map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                ).join(' ')
            ];

            // Add city context to search terms if available
            const searchCityContext = settings.destination || cityData?.city;
            if (searchCityContext && searchCityContext.toLowerCase() !== cleanedName.toLowerCase()) {
                variations.push(`${cleanedName} ${searchCityContext}`);
                variations.push(`${cleanedName}, ${searchCityContext}`);
            }

            // Remove duplicates
            const uniqueVariations = [...new Set(variations)];
            console.log(`🔍 Trying Wikipedia variations for "${cleanedName}":`, uniqueVariations);

            // Special debug for Basílica
            if (cleanedName.includes('Basílica') || cleanedName.includes('basilica')) {
                console.log(`🔍 BASILICA DEBUG - Original name: "${itemName}", localName: "${variations[0]}", all variations:`, uniqueVariations);
            }

            // Try local Wikipedia first, then English as fallback
            const wikipediaLanguages = wikiLang !== 'en' ? [wikiLang, 'en'] : ['en'];

            for (const lang of wikipediaLanguages) {
                console.log(`🔍 Searching ${lang}.wikipedia.org for ${itemType}...`);

                for (const variation of uniqueVariations) {
                    const wikiUrl = `https://${lang}.wikipedia.org/w/api.php?action=query&format=json&origin=*&prop=pageimages|pageterms&titles=${encodeURIComponent(variation)}&pithumbsize=400&piprop=thumbnail|name|original`;
                    console.log(`🌐 ${itemType.toUpperCase()}: Testing URL: ${wikiUrl}`);

                    try {
                        const response = await fetch(wikiUrl);
                        if (!response.ok) {
                            console.log(`❌ ${itemType.toUpperCase()}: HTTP error ${response.status} for "${variation}"`);
                            continue;
                        }

                        const data = await response.json();
                        console.log(`📖 ${itemType.toUpperCase()}: ${lang} Wikipedia API response for "${variation}":`, JSON.stringify(data, null, 2));

                        const pages = data.query?.pages;
                        if (!pages) continue;

                        const pageId = Object.keys(pages)[0];
                        const page = pages[pageId];

                        // Check for page existence (missing pages have negative IDs)
                        if (!page || pageId === '-1' || page.missing) {
                            console.log(`❌ ${lang} Wikipedia page not found for ${variation}`);
                            continue;
                        }

                        let imageUrl = null;
                        let originalUrl = null;

                        // Try to get image from different possible properties
                        if (page.thumbnail?.source) {
                            imageUrl = page.thumbnail.source;
                        }
                        if (page.original?.source) {
                            originalUrl = page.original.source;
                        }

                        if (imageUrl && isValidPhotoUrl(imageUrl)) {
                            console.log(`✅ Got ${lang} Wikipedia ${itemType} photo for ${variation}: ${imageUrl}`);

                            // Create a standardized photo object
                            const photoData = {
                                urls: {
                                    regular: imageUrl,
                                    original: originalUrl || imageUrl
                                },
                                src: {
                                    medium: imageUrl,
                                    original: originalUrl || imageUrl
                                },
                                alt_description: page.title || variation,
                                alt: page.title || variation,
                                photographer: `${lang.toUpperCase()} Wikipedia`,
                                user: { name: `${lang.toUpperCase()} Wikipedia` },
                                description: `${variation} from ${lang.toUpperCase()} Wikipedia`,
                                fallback: false,
                                source: 'wikipedia',
                                language: lang
                            };

                            setPhotoState(prev => ({ ...prev, [itemName]: photoData }));
                            return;
                        } else if (imageUrl) {
                            console.log(`🚫 Main image is SVG/invalid for ${variation}: ${imageUrl}`);

                            // Try to find a JPG/PNG photo in the page
                            const pagePhoto = await findPhotoInWikipediaPage(variation, lang);
                            if (pagePhoto) {
                                const photoData = {
                                    urls: {
                                        regular: pagePhoto.thumbnail,
                                        original: pagePhoto.original
                                    },
                                    src: {
                                        medium: pagePhoto.thumbnail,
                                        original: pagePhoto.original
                                    },
                                    alt_description: variation,
                                    alt: variation,
                                    photographer: `${lang.toUpperCase()} Wikipedia`,
                                    user: { name: `${lang.toUpperCase()} Wikipedia` },
                                    description: `${variation} from ${lang.toUpperCase()} Wikipedia`,
                                    fallback: false,
                                    source: 'wikipedia',
                                    language: lang
                                };

                                setPhotoState(prev => ({ ...prev, [itemName]: photoData }));
                                return;
                            }
                        } else {
                            console.log(`📄 ${lang} Wikipedia page exists for ${variation} but no image found`);
                        }
                    } catch (err) {
                        console.log(`❌ Error fetching from ${lang} Wikipedia for ${variation}:`, err.message);
                        continue;
                    }
                }
            }

            // If Wikipedia search fails, try Wikimedia Commons
            console.log(`📄 No Wikipedia images found, trying Commons fallback for ${itemType}`);

            // Create appropriate search terms for Commons based on item type
            const defaultSearchTerms = searchTerms || [
                `${cleanedName} ${searchCityContext || ''}`.trim(),
                `${cleanedName} ${itemType}`,
                cleanedName
            ];

            console.log(`📍 Search context: city="${searchCityContext}", country="${settings.country}"`);
            console.log(`🔍 Using search terms for ${itemType}:`, defaultSearchTerms);

            await tryCommonsSearch(cleanedName, itemName, defaultSearchTerms, setPhotoState);

        } catch (error) {
            console.error(`❌ Error fetching ${itemType} photo for ${itemName}:`, error);
            console.error(`❌ Error details:`, {
                name: error.name,
                message: error.message,
                stack: error.stack?.split('\n')[0]
            });
        } finally {
            setLoadingState(prev => ({ ...prev, [itemName]: false }));
            console.log(`✅ Finished photo fetch for ${itemType}: ${itemName}`);
        }
    };


    const saveBudgetData = (newBudgetData) => {
        setBudgetData(newBudgetData);
        Storage.set('budgetData', newBudgetData);
    };


    const updateBudget = (field, value) => {
        const updatedBudget = {
            ...budgetData,
            [field]: parseFloat(value) || 0
        };
        saveBudgetData(updatedBudget);
    };

    const updateCategoryBudget = (category, amount) => {
        const updatedBudget = {
            ...budgetData,
            categories: {
                ...budgetData.categories,
                [category]: (budgetData.categories[category] || 0) + parseFloat(amount)
            },
            spentToday: budgetData.spentToday + parseFloat(amount),
            totalSpent: budgetData.totalSpent + parseFloat(amount)
        };
        saveBudgetData(updatedBudget);
    };


    const openSchedulingModal = (activityName, category, description = '', mapsLink = '') => {
        setSelectedActivity({
            name: activityName,
            category: category,
            description: description,
            mapsLink: mapsLink
        });
        setSchedulingData({
            day: 1,
            period: 'morning'
        });
        setShowSchedulingModal(true);
    };

    const addActivityToTodo = (activityName, category, description = '', mapsLink = '', scheduledDay = null, scheduledPeriod = null) => {
        // Create a TO DO note that goes directly to trip notes
        const todoNote = {
            id: Date.now().toString(),
            type: 'todo',
            title: `TO DO: ${activityName}`,
            content: `${description}

**Location:** ${settings.destination || 'Current City'}
${scheduledDay && scheduledPeriod ? `**Scheduled:** Day ${scheduledDay} - ${scheduledPeriod.charAt(0).toUpperCase() + scheduledPeriod.slice(1)}` : ''}`,
            photos: [],
            tags: ['todo', category, 'activity'],
            date: new Date().toISOString().split('T')[0],
            time: new Date().toLocaleTimeString('en-US', { hour12: false }),
            tripDay: scheduledDay || getCurrentTripDay(),
            isActivity: true,
            activityCategory: category,
            mapsLink: mapsLink, // Store maps link for easy access
            scheduledDay: scheduledDay,
            scheduledPeriod: scheduledPeriod
        };

        // Add to trip notes
        const currentTripData = Storage.getTripData();
        currentTripData.notes = currentTripData.notes || [];
        currentTripData.notes.push(todoNote);

        // Add to timeline if scheduled
        if (scheduledDay && scheduledPeriod) {
            currentTripData.timeline = currentTripData.timeline || {};
            currentTripData.timeline[`day${scheduledDay}_${scheduledPeriod}`] = currentTripData.timeline[`day${scheduledDay}_${scheduledPeriod}`] || [];
            currentTripData.timeline[`day${scheduledDay}_${scheduledPeriod}`].push({
                id: todoNote.id,
                name: activityName,
                category: category,
                description: description,
                mapsLink: mapsLink
            });
        }

        Storage.saveTripData(currentTripData);

        // Show confirmation - debug the data being saved
        console.log('TO DO Note created:', todoNote);
        console.log('Current trip day:', getCurrentTripDay());
        console.log('Settings:', settings);

        // Show confirmation
        if (confirm(`✅ "${activityName}" added to your Trip Notes as a TO DO item!\n\nGo to Notes to see it?`)) {
            // Navigate to notes page with special parameter to go directly to book view
            onNavigate('notes', { directToBookView: true, selectedDay: scheduledDay || getCurrentTripDay() });
        }
    };

    const getCurrentTripDay = () => {
        if (!settings.startDate) return 1;
        const today = new Date();
        const tripStart = new Date(settings.startDate);
        const diffTime = today - tripStart;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return Math.max(1, diffDays);
    };

    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const openMaps = (mapsLink) => {
        if (mapsLink) {
            window.open(mapsLink, '_blank');
        }
    };

    const remainingBudget = budgetData.dailyBudget - budgetData.spentToday;

    return (
        <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto', background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)', minHeight: '100vh', position: 'relative' }}>
            <HomeButton onNavigate={onNavigate} />
            <h1 style={{ color: '#8B7355', marginBottom: '20px', textAlign: 'center', fontSize: '36px', fontWeight: '900', paddingTop: '60px', letterSpacing: '2px', fontFamily: "'Noto Serif', serif", textTransform: 'uppercase' }}>
                {t('plans.title')}
            </h1>

            {/* Tab Navigation */}
            <div style={{ 
                display: 'flex', 
                borderBottom: '2px solid #ede4d3', 
                marginBottom: '20px',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
                borderRadius: '12px 12px 0 0',
                padding: '10px'
            }}>
                {[
                    { key: 'inspiration', label: `${t('plans.whatToDoInCity')} ${settings.destination || t('mainPage.yourCity')}`, icon: '🗺️' },
                    { key: 'budget', label: t('plans.budgetTab'), icon: '💰' }
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveSection(tab.key)}
                        style={{
                            padding: '12px 24px',
                            border: 'none',
                            background: activeSection === tab.key ? '#5B8FA8' : 'transparent',
                            color: activeSection === tab.key ? 'white' : '#8B7355',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: '500',
                            transition: 'all 0.3s',
                            margin: '0 5px'
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Budget Section */}
            {activeSection === 'budget' && (
                <div style={{ 
                    background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)', 
                    padding: '20px', 
                    borderRadius: '12px', 
                    boxShadow: '0 4px 15px rgba(139, 115, 85, 0.1)',
                    marginBottom: '20px',
                    border: '1px solid #ede4d3'
                }}>
                    {/* Daily Budget Row */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '15px'
                    }}>
                        <label style={{ fontSize: '18px', fontWeight: '500', color: '#8B7355' }}>
                            {t('plans.dailyBudget')}
                        </label>
                        <input
                            type="number"
                            value={budgetData.dailyBudget}
                            onChange={(e) => updateBudget('dailyBudget', e.target.value)}
                            style={{
                                width: '120px',
                                padding: '8px 12px',
                                border: '2px solid #B8A082',
                                borderRadius: '8px',
                                fontSize: '16px',
                                backgroundColor: 'white',
                                color: '#8B7355',
                                textAlign: 'right'
                            }}
                            placeholder="0"
                        />
                    </div>

                    {/* Spent Today Row */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '15px'
                    }}>
                        <label style={{ fontSize: '18px', fontWeight: '500', color: '#8B7355' }}>
                            {t('plans.spentToday')}
                        </label>
                        <div style={{
                            width: '120px',
                            padding: '8px 12px',
                            backgroundColor: 'white',
                            border: '2px solid #B8A082',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            color: budgetData.spentToday > budgetData.dailyBudget ? '#A67C5A' : '#8B7355',
                            textAlign: 'right'
                        }}>
                            {budgetData.spentToday.toFixed(0)}
                        </div>
                    </div>

                    {/* Remaining Today Row */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px'
                    }}>
                        <label style={{ fontSize: '18px', fontWeight: '500', color: '#8B7355' }}>
                            {t('plans.remainingToday')}
                        </label>
                        <div style={{
                            width: '120px',
                            padding: '8px 12px',
                            backgroundColor: remainingBudget < 0 ? '#f5e6e0' : 'white',
                            border: '2px solid #B8A082',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            color: remainingBudget < 0 ? '#A67C5A' : '#8B7355',
                            textAlign: 'right'
                        }}>
                            {remainingBudget.toFixed(0)}
                        </div>
                    </div>

                    {/* Expense Categories Title */}
                    <h3 style={{ color: '#8B7355', marginBottom: '15px', fontSize: '20px', fontWeight: '600' }}>Expense Categories</h3>
                    
                    {/* Expense Categories */}
                    {Object.entries(budgetData.categories).map(([category, amount]) => (
                        <div key={category} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '12px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                <span style={{ 
                                    fontSize: '20px', 
                                    marginRight: '8px' 
                                }}>
                                    {category === 'food' ? '🍽️' : 
                                     category === 'transport' ? '🚗' : 
                                     category === 'activities' ? '🎯' : 
                                     category === 'shopping' ? '🛍️' : 
                                     category === 'accommodation' ? '🏨' : '💳'}
                                </span>
                                <span style={{ 
                                    fontWeight: '500', 
                                    textTransform: 'capitalize', 
                                    color: '#8B7355',
                                    fontSize: '16px'
                                }}>
                                    {category}
                                </span>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="number"
                                    placeholder="Amount"
                                    style={{
                                        width: '80px',
                                        padding: '6px 8px',
                                        border: '2px solid #B8A082',
                                        borderRadius: '6px',
                                        backgroundColor: 'white',
                                        color: '#8B7355',
                                        fontSize: '14px',
                                        textAlign: 'right'
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            updateCategoryBudget(category, e.target.value);
                                            e.target.value = '';
                                        }
                                    }}
                                />
                                <button
                                    onClick={(e) => {
                                        const input = e.target.parentNode.querySelector('input');
                                        if (input.value) {
                                            updateCategoryBudget(category, input.value);
                                            input.value = '';
                                        }
                                    }}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#C4714A',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: '500',
                                        fontSize: '14px'
                                    }}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Inspiration Section */}
            {activeSection === 'inspiration' && (
                <div>
                    {!cityData ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <p>Loading city data...</p>
                        </div>
                    ) : (
                        <div>
                            <h2 style={{ color: '#8B7355', marginBottom: '20px', textAlign: 'center', fontSize: '28px', fontWeight: '600' }}>
                                ✨ {t('plans.whatToDoInCity')} {cityData.city}
                            </h2>
                            
                            {/* City Information */}
                            <div style={{ 
                                background: 'white', 
                                padding: '20px', 
                                marginBottom: '20px',
                                borderRadius: '12px',
                                border: cityData.isWebSearchData ? '2px solid #E67E22' : '2px solid #B8A082'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <h3 style={{ color: '#8B7355', margin: 0, fontSize: '18px', fontWeight: '600' }}>
                                        About {cityData.city}
                                    </h3>
                                    {cityData.isWebSearchData && (
                                        <div style={{
                                            background: cityData.loading ? '#F39C12' : cityData.webSearchFailed ? '#E74C3C' : '#27AE60',
                                            color: 'white',
                                            padding: '4px 8px',
                                            borderRadius: '12px',
                                            fontSize: '10px',
                                            fontWeight: '500',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            {cityData.loading ? '🔄 Searching...' : 
                                             cityData.webSearchFailed ? '📡 Basic Info' : 
                                             '🌐 Web Enhanced'}
                                        </div>
                                    )}
                                </div>

                                {/* Unsplash Photo Section */}
                                <div style={{ marginBottom: '15px' }}>
                                    {unsplashLoading ? (
                                        <div style={{
                                            width: '100%',
                                            height: '200px',
                                            background: 'linear-gradient(45deg, #f5f1eb, #ede4d3)',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '1px solid rgba(184, 160, 130, 0.3)'
                                        }}>
                                            <div style={{ textAlign: 'center', color: '#B8A082' }}>
                                                <div style={{ fontSize: '24px', marginBottom: '8px' }}>📸</div>
                                                <div style={{ fontSize: '12px', fontWeight: '500' }}>Loading city photo...</div>
                                            </div>
                                        </div>
                                    ) : unsplashPhoto ? (
                                        <div style={{ position: 'relative' }}>
                                            <img
                                                src={unsplashPhoto.urls.regular}
                                                alt={unsplashPhoto.alt_description || `Beautiful view of ${cityData.city}`}
                                                style={{
                                                    width: '100%',
                                                    height: '200px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    border: '1px solid rgba(184, 160, 130, 0.3)'
                                                }}
                                                onError={(e) => {
                                                    e.target.src = `https://picsum.photos/400/200?random=${Math.floor(Math.random() * 1000)}`;
                                                }}
                                            />
                                            <div style={{
                                                position: 'absolute',
                                                bottom: '8px',
                                                right: '8px',
                                                background: 'rgba(0, 0, 0, 0.8)',
                                                color: 'white',
                                                padding: '6px 10px',
                                                borderRadius: '6px',
                                                fontSize: '10px',
                                                fontWeight: '500',
                                                maxWidth: 'calc(100% - 16px)',
                                                textAlign: 'right'
                                            }}>
                                                {unsplashPhoto.fallback ? (
                                                    <div>
                                                        <div>📷 {unsplashPhoto.user?.name || 'Curated Photo'}</div>
                                                        <div style={{
                                                            fontSize: '8px',
                                                            opacity: 0.8,
                                                            marginTop: '2px'
                                                        }}>
                                                            {unsplashPhoto.source === 'wikipedia' ? 'Wikipedia Commons' :
                                                             unsplashPhoto.user?.name?.includes('Photography') ? 'City Collection' :
                                                             'Curated Photos'}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div>📷 Photo by {unsplashPhoto.user?.name || 'Unknown'}</div>
                                                        <div style={{
                                                            fontSize: '8px',
                                                            opacity: 0.8,
                                                            marginTop: '2px'
                                                        }}>
                                                            {unsplashPhoto.source === 'pexels' ? 'on Pexels' : 'on Unsplash'}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : unsplashError && (
                                        <div style={{
                                            width: '100%',
                                            height: '100px',
                                            background: '#f8f9fa',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '1px solid rgba(184, 160, 130, 0.3)',
                                            color: '#8B7355'
                                        }}>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ fontSize: '18px', marginBottom: '4px' }}>🌟</div>
                                                <div style={{ fontSize: '12px' }}>Photo coming soon</div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {cityData.briefDescription && (
                                    <div style={{ marginBottom: '15px' }}>
                                        <p style={{ 
                                            margin: 0, 
                                            fontSize: '14px', 
                                            color: '#8B7355', 
                                            lineHeight: '1.5'
                                        }}>
                                            {cityData.briefDescription}
                                        </p>
                                    </div>
                                )}

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                                    {cityData.climate && (
                                        <div>
                                            <h4 style={{ color: '#8B7355', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                                                🌡️ Climate
                                            </h4>
                                            <p style={{ margin: 0, fontSize: '13px', color: '#A67C5A' }}>
                                                {cityData.climate}
                                            </p>
                                        </div>
                                    )}

                                    {cityData.currency && (
                                        <div>
                                            <h4 style={{ color: '#8B7355', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                                                💱 Currency
                                            </h4>
                                            <p style={{ margin: 0, fontSize: '13px', color: '#A67C5A' }}>
                                                {cityData.currency}
                                            </p>
                                        </div>
                                    )}

                                    {cityData.timezone && (
                                        <div>
                                            <h4 style={{ color: '#8B7355', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                                                🕐 Timezone
                                            </h4>
                                           <p style={{ margin: 0, fontSize: '13px', color: '#A67C5A' }}>
  {cityData.timezone.standardTime}
  {cityData.timezone.daylightSavingTime && (
    <>
      <br />
      {cityData.timezone.daylightSavingTime}
    </>
  )}
</p>
                                        </div>
                                    )}

                                    {cityData.languagesSpoken && (
                                        <div>
                                            <h4 style={{ color: '#8B7355', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                                                🗣️ Languages
                                            </h4>
                                            <p style={{ margin: 0, fontSize: '13px', color: '#A67C5A' }}>
                                                <strong>Official:</strong> {cityData.languagesSpoken.officialLanguage}
                                                {cityData.languagesSpoken.widelySpokenLanguages && cityData.languagesSpoken.widelySpokenLanguages.length > 0 && (
                                                    <>
                                                        <br/>
                                                        <strong>Spoken:</strong> {cityData.languagesSpoken.widelySpokenLanguages.map(lang => 
                                                            typeof lang === 'object' ? `${lang.language} (${lang.percentageSpoken || ''})` : lang
                                                        ).join(', ')}
                                                    </>
                                                )}
                                                {cityData.languagesSpoken.note && (
                                                    <>
                                                        <br/>
                                                        <em>{cityData.languagesSpoken.note}</em>
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                   {/* Food Section */}
{(cityData.typicalFoodsCity?.length > 0 || cityData.typicalFoodsCountry?.length > 0) && (
  <div
    style={{
      background: 'white',
      padding: '20px',
      borderRadius: '10px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      marginBottom: '20px'
    }}
  >
    <div
      onClick={() => toggleCategory('food')}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        marginBottom: '15px'
      }}
    >
      <h3 style={{ color: '#8B7355', margin: 0, fontSize: '22px', fontWeight: '600' }}>
        {t('plans.localFood')}
      </h3>
      <span style={{ fontSize: '20px', color: '#B8A082' }}>
        {expandedCategories.food ? '▼' : '▶'}
      </span>
    </div>

    {expandedCategories.food && (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '15px'
        }}
      >
        {cityData.typicalFoodsCity?.map((food, index) => (
          <div
            key={index}
            data-activity-name={food.name}
            style={{
              border: '2px solid #B8A082',
              borderRadius: '12px',
              backgroundColor: 'white',
              overflow: 'hidden'
            }}
          >
            {/* Food Photo */}
            <div style={{ height: '150px', position: 'relative', overflow: 'hidden' }}>
              {loadingFoodPhotos[food.name] ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(45deg, #f5f1eb, #ede4d3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#B8A082'
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', marginBottom: '5px' }}>🍽️</div>
                    <div style={{ fontSize: '11px' }}>Loading...</div>
                  </div>
                </div>
              ) : foodPhotos[food.name] ? (
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                  <img
                    src={foodPhotos[food.name].urls?.regular || foodPhotos[food.name].src?.medium}
                    alt={foodPhotos[food.name].alt_description || foodPhotos[food.name].alt || food.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      // If the main image fails, try the original URL or hide
                      const originalSrc = foodPhotos[food.name].urls?.original || foodPhotos[food.name].src?.original;
                      if (originalSrc && e.target.src !== originalSrc) {
                        e.target.src = originalSrc;
                      } else {
                        e.target.style.display = 'none';
                        // Show a fallback placeholder
                        const placeholder = e.target.parentNode.querySelector('.photo-placeholder');
                        if (placeholder) placeholder.style.display = 'flex';
                      }
                    }}
                  />
                  <div
                    className="photo-placeholder"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: '#f8f9fa',
                      display: 'none',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#8B7355',
                      flexDirection: 'column'
                    }}
                  >
                    <div style={{ fontSize: '20px', marginBottom: '5px' }}>🍽️</div>
                    <div style={{ fontSize: '11px' }}>{food.name}</div>
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '5px',
                      right: '5px',
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '8px'
                    }}
                  >
                    📷 {foodPhotos[food.name].user?.name || foodPhotos[food.name].photographer}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#8B7355'
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', marginBottom: '5px' }}>🍽️</div>
                    <div style={{ fontSize: '11px' }}>{food.name}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Food Content */}
            <div style={{ padding: '15px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '10px'
                }}
              >
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      margin: '0 0 5px 0',
                      color: '#8B7355',
                      fontWeight: '600'
                    }}
                  >
                    {food.name}
                  </h4>
                  <p style={{ margin: 0, fontSize: '14px', color: '#A67C5A' }}>
                    {food.description}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '5px', marginLeft: '10px' }}>
                  {food.mapsLink && (
                    <button
                      onClick={() => openMaps(food.mapsLink)}
                      style={{
                        padding: '6px 10px',
                        backgroundColor: '#8B7355',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      📍
                    </button>
                  )}
                  <button
                    onClick={() =>
                      openSchedulingModal(food.name, 'food', food.description, food.mapsLink)
                    }
                    style={{
                      padding: '6px 10px',
                      backgroundColor: '#C4714A',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    📝
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {cityData.typicalFoodsCountry?.map((food, index) => (
          <div
            key={`country-${index}`}
            style={{
              border: '2px solid #f39c12',
              borderRadius: '12px',
              backgroundColor: '#fef9e7',
              overflow: 'hidden'
            }}
          >
            {/* Country Food Photo */}
            <div style={{ height: '150px', position: 'relative', overflow: 'hidden' }}>
              {loadingFoodPhotos[food.name] ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(45deg, #fef9e7, #f7dc6f)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#f39c12'
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', marginBottom: '5px' }}>🍽️</div>
                    <div style={{ fontSize: '11px' }}>Loading...</div>
                  </div>
                </div>
              ) : foodPhotos[food.name] ? (
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                  <img
                    src={foodPhotos[food.name].urls?.regular || foodPhotos[food.name].src?.medium}
                    alt={foodPhotos[food.name].alt_description || foodPhotos[food.name].alt || food.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      // If the main image fails, try the original URL or hide
                      const originalSrc = foodPhotos[food.name].urls?.original || foodPhotos[food.name].src?.original;
                      if (originalSrc && e.target.src !== originalSrc) {
                        e.target.src = originalSrc;
                      } else {
                        e.target.style.display = 'none';
                        // Show a fallback placeholder
                        const placeholder = e.target.parentNode.querySelector('.photo-placeholder');
                        if (placeholder) placeholder.style.display = 'flex';
                      }
                    }}
                  />
                  <div
                    className="photo-placeholder"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: '#f7dc6f',
                      display: 'none',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#d68910',
                      flexDirection: 'column'
                    }}
                  >
                    <div style={{ fontSize: '20px', marginBottom: '5px' }}>🍽️</div>
                    <div style={{ fontSize: '11px' }}>{food.name}</div>
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '5px',
                      right: '5px',
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '8px'
                    }}
                  >
                    📷 {foodPhotos[food.name].user?.name || foodPhotos[food.name].photographer}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: '#f7dc6f',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#d68910'
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', marginBottom: '5px' }}>🍽️</div>
                    <div style={{ fontSize: '11px' }}>{food.name}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Country Food Content */}
            <div style={{ padding: '15px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '10px'
                }}
              >
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 5px 0', color: '#d68910', fontWeight: '600' }}>
                    {food.name}
                  </h4>
                  <p style={{ margin: 0, fontSize: '14px', color: '#7d6608' }}>
                    {food.description}
                  </p>
                </div>
                <button
                  onClick={() => openSchedulingModal(food.name, 'food', food.description)}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: '#f39c12',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  📝
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
)}


                    {/* Cultural Attractions */}
                    {cityData.culturalAttractions?.length > 0 && (
                        <div style={{ 
                            background: 'white', 
                            padding: '20px', 
                            borderRadius: '10px', 
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            marginBottom: '20px'
                        }}>
                            <div 
                                onClick={() => toggleCategory('cultural')}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    marginBottom: '15px'
                                }}
                            >
                                <h3 style={{ color: '#9b59b6', margin: 0 }}>{t('plans.culturalAttractions')}</h3>
                                <span style={{ fontSize: '20px' }}>{expandedCategories.cultural ? '▼' : '▶'}</span>
                            </div>
                            
                            {expandedCategories.cultural && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                                    {cityData.culturalAttractions.map((attraction, index) => (
                                        <div key={index} data-activity-name={attraction.name} style={{
                                            border: '2px solid #8e44ad',
                                            borderRadius: '12px',
                                            backgroundColor: '#faf2ff',
                                            overflow: 'hidden'
                                        }}>
                                            {/* Attraction Photo */}
                                            <div style={{ height: '150px', position: 'relative', overflow: 'hidden' }}>
                                                {loadingAttractionPhotos[attraction.name] ? (
                                                    <div style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        background: 'linear-gradient(45deg, #faf2ff, #e8d5ff)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#8e44ad'
                                                    }}>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: '20px', marginBottom: '5px' }}>🏛️</div>
                                                            <div style={{ fontSize: '11px' }}>Loading...</div>
                                                        </div>
                                                    </div>
                                                ) : attractionPhotos[attraction.name] ? (
                                                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                        <img
                                                            src={attractionPhotos[attraction.name].urls?.regular || attractionPhotos[attraction.name].src?.medium}
                                                            alt={attractionPhotos[attraction.name].alt_description || attractionPhotos[attraction.name].alt || attraction.name}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                            onError={(e) => {
                                                                const originalSrc = attractionPhotos[attraction.name].urls?.original || attractionPhotos[attraction.name].src?.original;
                                                                if (originalSrc && e.target.src !== originalSrc) {
                                                                    e.target.src = originalSrc;
                                                                } else {
                                                                    e.target.style.display = 'none';
                                                                    const placeholder = e.target.parentNode.querySelector('.photo-placeholder');
                                                                    if (placeholder) placeholder.style.display = 'flex';
                                                                }
                                                            }}
                                                        />
                                                        <div className="photo-placeholder" style={{
                                                            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                                            background: '#e8d5ff', display: 'none', alignItems: 'center', justifyContent: 'center',
                                                            color: '#7d3c98', flexDirection: 'column'
                                                        }}>
                                                            <div style={{ fontSize: '20px', marginBottom: '5px' }}>🏛️</div>
                                                            <div style={{ fontSize: '11px' }}>{attraction.name}</div>
                                                        </div>
                                                        <div style={{
                                                            position: 'absolute', bottom: '5px', right: '5px',
                                                            background: 'rgba(0, 0, 0, 0.7)', color: 'white',
                                                            padding: '2px 6px', borderRadius: '3px', fontSize: '8px'
                                                        }}>
                                                            📷 {attractionPhotos[attraction.name].user?.name || attractionPhotos[attraction.name].photographer}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{
                                                        width: '100%', height: '100%', background: '#e8d5ff',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7d3c98'
                                                    }}>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: '20px', marginBottom: '5px' }}>🏛️</div>
                                                            <div style={{ fontSize: '11px' }}>{attraction.name}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Attraction Content */}
                                            <div style={{ padding: '15px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                                    <div style={{ flex: 1 }}>
                                                        <h4 style={{ margin: '0 0 5px 0', color: '#7d3c98', fontWeight: '600' }}>{attraction.name}</h4>
                                                        <p style={{ margin: 0, fontSize: '14px', color: '#5b2c6f' }}>{attraction.description}</p>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '5px', marginLeft: '10px' }}>
                                                        {attraction.mapsLink && (
                                                            <button
                                                                onClick={() => openMaps(attraction.mapsLink)}
                                                                style={{
                                                                    padding: '6px 10px',
                                                                    backgroundColor: '#7d3c98',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500'
                                                                }}
                                                            >
                                                                📍
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => openSchedulingModal(attraction.name, 'cultural', attraction.description, attraction.mapsLink)}
                                                            style={{
                                                                padding: '6px 10px',
                                                                backgroundColor: '#8e44ad',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            📝
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Monuments */}
                    {cityData.monuments?.length > 0 && (
                        <div style={{ 
                            background: 'white', 
                            padding: '20px', 
                            borderRadius: '10px', 
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            marginBottom: '20px'
                        }}>
                            <div 
                                onClick={() => toggleCategory('monuments')}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    marginBottom: '15px'
                                }}
                            >
                                <h3 style={{ color: '#e74c3c', margin: 0 }}>{t('plans.monuments')}</h3>
                                <span style={{ fontSize: '20px' }}>{expandedCategories.monuments ? '▼' : '▶'}</span>
                            </div>
                            
                            {expandedCategories.monuments && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                                    {cityData.monuments.map((monument, index) => (
                                        <div key={index} data-activity-name={monument.name} style={{
                                            border: '2px solid #c0392b',
                                            borderRadius: '12px',
                                            backgroundColor: '#fdedec',
                                            overflow: 'hidden'
                                        }}>
                                            {/* Monument Photo */}
                                            <div style={{ height: '150px', position: 'relative', overflow: 'hidden' }}>
                                                {loadingMonumentPhotos[monument.name] ? (
                                                    <div style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        background: 'linear-gradient(45deg, #fdedec, #f5b7b1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#c0392b'
                                                    }}>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: '20px', marginBottom: '5px' }}>🏰</div>
                                                            <div style={{ fontSize: '11px' }}>Loading...</div>
                                                        </div>
                                                    </div>
                                                ) : monumentPhotos[monument.name] ? (
                                                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                        <img
                                                            src={monumentPhotos[monument.name].urls?.regular || monumentPhotos[monument.name].src?.medium}
                                                            alt={monumentPhotos[monument.name].alt_description || monumentPhotos[monument.name].alt || monument.name}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                            onError={(e) => {
                                                                const originalSrc = monumentPhotos[monument.name].urls?.original || monumentPhotos[monument.name].src?.original;
                                                                if (originalSrc && e.target.src !== originalSrc) {
                                                                    e.target.src = originalSrc;
                                                                } else {
                                                                    e.target.style.display = 'none';
                                                                    const placeholder = e.target.parentNode.querySelector('.photo-placeholder');
                                                                    if (placeholder) placeholder.style.display = 'flex';
                                                                }
                                                            }}
                                                        />
                                                        <div className="photo-placeholder" style={{
                                                            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                                            background: '#f5b7b1', display: 'none', alignItems: 'center', justifyContent: 'center',
                                                            color: '#a93226', flexDirection: 'column'
                                                        }}>
                                                            <div style={{ fontSize: '20px', marginBottom: '5px' }}>🏰</div>
                                                            <div style={{ fontSize: '11px' }}>{monument.name}</div>
                                                        </div>
                                                        <div style={{
                                                            position: 'absolute', bottom: '5px', right: '5px',
                                                            background: 'rgba(0, 0, 0, 0.7)', color: 'white',
                                                            padding: '2px 6px', borderRadius: '3px', fontSize: '8px'
                                                        }}>
                                                            📷 {monumentPhotos[monument.name].user?.name || monumentPhotos[monument.name].photographer}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{
                                                        width: '100%', height: '100%', background: '#f5b7b1',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a93226'
                                                    }}>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: '20px', marginBottom: '5px' }}>🏰</div>
                                                            <div style={{ fontSize: '11px' }}>{monument.name}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Monument Content */}
                                            <div style={{ padding: '15px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                                    <div style={{ flex: 1 }}>
                                                        <h4 style={{ margin: '0 0 5px 0', color: '#a93226', fontWeight: '600' }}>{monument.name}</h4>
                                                        <p style={{ margin: 0, fontSize: '14px', color: '#78281f' }}>{monument.description}</p>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '5px', marginLeft: '10px' }}>
                                                        {monument.mapsLink && (
                                                            <button
                                                                onClick={() => openMaps(monument.mapsLink)}
                                                                style={{
                                                                    padding: '6px 10px',
                                                                    backgroundColor: '#a93226',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500'
                                                                }}
                                                            >
                                                                📍
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => openSchedulingModal(monument.name, 'monuments', monument.description, monument.mapsLink)}
                                                            style={{
                                                                padding: '6px 10px',
                                                                backgroundColor: '#c0392b',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            📝
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Local Activities */}
                    {cityData.localActivities?.length > 0 && (
                        <div style={{ 
                            background: 'white', 
                            padding: '20px', 
                            borderRadius: '10px', 
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            marginBottom: '20px'
                        }}>
                            <div 
                                onClick={() => toggleCategory('activities')}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    marginBottom: '15px'
                                }}
                            >
                                <h3 style={{ color: '#f39c12', margin: 0 }}>{t('plans.localActivities')}</h3>
                                <span style={{ fontSize: '20px' }}>{expandedCategories.activities ? '▼' : '▶'}</span>
                            </div>
                            
                            {expandedCategories.activities && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                                    {cityData.localActivities.map((activity, index) => (
                                        <div key={index} data-activity-name={activity.name} style={{
                                            border: '2px solid #e67e22',
                                            borderRadius: '12px',
                                            backgroundColor: '#fef5e7',
                                            overflow: 'hidden'
                                        }}>
                                            {/* Activity Photo */}
                                            <div style={{ height: '150px', position: 'relative', overflow: 'hidden' }}>
                                                {loadingActivityPhotos[activity.name] ? (
                                                    <div style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        background: 'linear-gradient(45deg, #fef5e7, #f8c471)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#e67e22'
                                                    }}>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: '20px', marginBottom: '5px' }}>🎯</div>
                                                            <div style={{ fontSize: '11px' }}>Loading...</div>
                                                        </div>
                                                    </div>
                                                ) : activityPhotos[activity.name] ? (
                                                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                        <img
                                                            src={activityPhotos[activity.name].urls?.regular || activityPhotos[activity.name].src?.medium}
                                                            alt={activityPhotos[activity.name].alt_description || activityPhotos[activity.name].alt || activity.name}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                            onError={(e) => {
                                                                const originalSrc = activityPhotos[activity.name].urls?.original || activityPhotos[activity.name].src?.original;
                                                                if (originalSrc && e.target.src !== originalSrc) {
                                                                    e.target.src = originalSrc;
                                                                } else {
                                                                    e.target.style.display = 'none';
                                                                    const placeholder = e.target.parentNode.querySelector('.photo-placeholder');
                                                                    if (placeholder) placeholder.style.display = 'flex';
                                                                }
                                                            }}
                                                        />
                                                        <div className="photo-placeholder" style={{
                                                            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                                            background: '#f8c471', display: 'none', alignItems: 'center', justifyContent: 'center',
                                                            color: '#d68910', flexDirection: 'column'
                                                        }}>
                                                            <div style={{ fontSize: '20px', marginBottom: '5px' }}>🎯</div>
                                                            <div style={{ fontSize: '11px' }}>{activity.name}</div>
                                                        </div>
                                                        <div style={{
                                                            position: 'absolute', bottom: '5px', right: '5px',
                                                            background: 'rgba(0, 0, 0, 0.7)', color: 'white',
                                                            padding: '2px 6px', borderRadius: '3px', fontSize: '8px'
                                                        }}>
                                                            📷 {activityPhotos[activity.name].user?.name || activityPhotos[activity.name].photographer}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{
                                                        width: '100%', height: '100%', background: '#f8c471',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d68910'
                                                    }}>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: '20px', marginBottom: '5px' }}>🎯</div>
                                                            <div style={{ fontSize: '11px' }}>{activity.name}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Activity Content */}
                                            <div style={{ padding: '15px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                                    <div style={{ flex: 1 }}>
                                                        <h4 style={{ margin: '0 0 5px 0', color: '#d68910', fontWeight: '600' }}>{activity.name}</h4>
                                                        <p style={{ margin: 0, fontSize: '14px', color: '#b7950b' }}>{activity.description}</p>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '5px', marginLeft: '10px' }}>
                                                        {activity.mapsLink && (
                                                            <button
                                                                onClick={() => openMaps(activity.mapsLink)}
                                                                style={{
                                                                    padding: '6px 10px',
                                                                    backgroundColor: '#d68910',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500'
                                                                }}
                                                            >
                                                                📍
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => openSchedulingModal(activity.name, 'activities', activity.description, activity.mapsLink)}
                                                            style={{
                                                                padding: '6px 10px',
                                                                backgroundColor: '#e67e22',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            📝
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Traditional Locals */}
                    {cityData.traditionalLocals?.length > 0 && (
                        <div style={{ 
                            background: 'white', 
                            padding: '20px', 
                            borderRadius: '10px', 
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            marginBottom: '20px'
                        }}>
                            <div 
                                onClick={() => toggleCategory('locals')}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    marginBottom: '15px'
                                }}
                            >
                                <h3 style={{ color: '#16a085', margin: 0 }}>🏘️ Traditional Neighborhoods</h3>
                                <span style={{ fontSize: '20px' }}>{expandedCategories.locals ? '▼' : '▶'}</span>
                            </div>
                            
                            {expandedCategories.locals && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                                    {cityData.traditionalLocals.map((local, index) => (
                                        <div key={index} style={{
                                            border: '2px solid #138d75',
                                            borderRadius: '12px',
                                            backgroundColor: '#e8f8f5',
                                            overflow: 'hidden'
                                        }}>
                                            {/* Neighborhood Photo */}
                                            <div style={{ height: '150px', position: 'relative', overflow: 'hidden' }}>
                                                {loadingNeighborhoodPhotos[local.name] ? (
                                                    <div style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        background: 'linear-gradient(45deg, #e8f8f5, #a3e4d7)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#138d75'
                                                    }}>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: '20px', marginBottom: '5px' }}>🏘️</div>
                                                            <div style={{ fontSize: '11px' }}>Loading...</div>
                                                        </div>
                                                    </div>
                                                ) : neighborhoodPhotos[local.name] ? (
                                                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                        <img
                                                            src={neighborhoodPhotos[local.name].urls?.regular || neighborhoodPhotos[local.name].src?.medium}
                                                            alt={neighborhoodPhotos[local.name].alt_description || neighborhoodPhotos[local.name].alt || local.name}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                            onError={(e) => {
                                                                const originalSrc = neighborhoodPhotos[local.name].urls?.original || neighborhoodPhotos[local.name].src?.original;
                                                                if (originalSrc && e.target.src !== originalSrc) {
                                                                    e.target.src = originalSrc;
                                                                } else {
                                                                    e.target.style.display = 'none';
                                                                    const placeholder = e.target.parentNode.querySelector('.photo-placeholder');
                                                                    if (placeholder) placeholder.style.display = 'flex';
                                                                }
                                                            }}
                                                        />
                                                        <div className="photo-placeholder" style={{
                                                            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                                            background: '#a3e4d7', display: 'none', alignItems: 'center', justifyContent: 'center',
                                                            color: '#117a65', flexDirection: 'column'
                                                        }}>
                                                            <div style={{ fontSize: '20px', marginBottom: '5px' }}>🏘️</div>
                                                            <div style={{ fontSize: '11px' }}>{local.name}</div>
                                                        </div>
                                                        <div style={{
                                                            position: 'absolute', bottom: '5px', right: '5px',
                                                            background: 'rgba(0, 0, 0, 0.7)', color: 'white',
                                                            padding: '2px 6px', borderRadius: '3px', fontSize: '8px'
                                                        }}>
                                                            📷 {neighborhoodPhotos[local.name].user?.name || neighborhoodPhotos[local.name].photographer}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{
                                                        width: '100%', height: '100%', background: '#a3e4d7',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#117a65'
                                                    }}>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: '20px', marginBottom: '5px' }}>🏘️</div>
                                                            <div style={{ fontSize: '11px' }}>{local.name}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Neighborhood Content */}
                                            <div style={{ padding: '15px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                                    <div style={{ flex: 1 }}>
                                                        <h4 style={{ margin: '0 0 5px 0', color: '#117a65', fontWeight: '600' }}>{local.name}</h4>
                                                        <p style={{ margin: 0, fontSize: '14px', color: '#0e6655' }}>{local.description}</p>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '5px', marginLeft: '10px' }}>
                                                        {local.mapsLink && (
                                                            <button
                                                                onClick={() => openMaps(local.mapsLink)}
                                                                style={{
                                                                    padding: '6px 10px',
                                                                    backgroundColor: '#117a65',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500'
                                                                }}
                                                            >
                                                                📍
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => openSchedulingModal(local.name, 'locals', local.description, local.mapsLink)}
                                                            style={{
                                                                padding: '6px 10px',
                                                                backgroundColor: '#138d75',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            📝
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                        </div>
                    )}
                </div>
            )}


                    {/* Community Spots */}
                    {communitySpots.length > 0 && (
                        <div style={{
                            background: 'white',
                            padding: '20px',
                            borderRadius: '10px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            marginBottom: '20px'
                        }}>
                            <div
                                onClick={() => toggleCategory('community')}
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: '15px' }}
                            >
                                <h3 style={{ color: '#8B7355', margin: 0 }}>📍 {t('community.sectionTitle', 'Discovered by the Community')}</h3>
                                <span style={{ fontSize: '20px' }}>{expandedCategories.community ? '▼' : '▶'}</span>
                            </div>
                            {expandedCategories.community && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                                    {communitySpots.map((spot, index) => {
                                        const categoryColors = { cultural: '#8e44ad', monuments: '#c0392b', activities: '#e67e22', locals: '#16a085', food: '#d4a017' };
                                        const categoryEmojis = { cultural: '🎭', monuments: '🏛️', activities: '🏄', locals: '🏘️', food: '🍽️' };
                                        const color = categoryColors[spot.category] || '#8B7355';
                                        const emoji = categoryEmojis[spot.category] || '📍';
                                        return (
                                            <div key={spot.submission_id || index} style={{ border: `2px solid ${color}`, borderRadius: '12px', backgroundColor: '#fdfaf5', overflow: 'hidden' }}>
                                                <div style={{ height: '80px', background: `linear-gradient(135deg, ${color}22, ${color}44)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <span style={{ fontSize: '36px' }}>{emoji}</span>
                                                </div>
                                                <div style={{ padding: '15px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                                <h4 style={{ margin: 0, color: '#4a3728', fontWeight: '600' }}>{spot.name}</h4>
                                                                <span style={{ fontSize: '10px', background: color, color: 'white', padding: '2px 6px', borderRadius: '10px', whiteSpace: 'nowrap' }}>{spot.category}</span>
                                                            </div>
                                                            <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#6b5744' }}>{spot.description}</p>
                                                            <p style={{ margin: 0, fontSize: '11px', color: '#aaa' }}>
                                                                {/* Sprint 3 — opt-in attribution; anonymous by default (never leak submitter_name) */}
                                                                {t('community.submittedBy', 'Shared by')}{' '}
                                                                {(Number(spot.attribution_optin) && spot.contributor_handle)
                                                                    ? spot.contributor_handle
                                                                    : t('community.anonTraveller', 'a traveller')}
                                                            </p>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '5px', marginLeft: '10px' }}>
                                                            {spot.maps_link && (
                                                                <button
                                                                    onClick={() => openMaps(spot.maps_link)}
                                                                    style={{ padding: '6px 10px', backgroundColor: color, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}
                                                                >
                                                                    📍
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => openSchedulingModal(spot.name, spot.category, spot.description, spot.maps_link)}
                                                                style={{ padding: '6px 10px', backgroundColor: '#8B7355', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}
                                                            >
                                                                📝
                                                            </button>
                                                            {/* Sprint 3 §3c — gift this spot (offline deep link) */}
                                                            <button
                                                                title={t('gift.giftThis', 'Gift this')}
                                                                onClick={() => setGiftSpot({
                                                                    type: 'spot',
                                                                    data: {
                                                                        name: spot.name,
                                                                        description: spot.description,
                                                                        category: spot.category,
                                                                        mapsLink: spot.maps_link || '',
                                                                        city: userData?.destination || '',
                                                                        country: userData?.country || '',
                                                                    },
                                                                    from: (Number(spot.attribution_optin) && spot.contributor_handle) ? spot.contributor_handle : null,
                                                                })}
                                                                style={{ padding: '6px 10px', backgroundColor: '#C4714A', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}
                                                            >
                                                                🎁
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

       {giftSpot && <GiftModal gift={giftSpot} onClose={() => setGiftSpot(null)} />}

       {/* Back Button */}
      <div style={{
        textAlign: 'center'
      }}>
        <button
          style={{
            background: 'transparent',
            color: '#8B7355',
            border: '2px solid #8B7355',
            padding: '12px 24px',
            borderRadius: '25px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
          onClick={() => onNavigate('home')}
        >
          {t('navigation.backToHome')}
        </button>
      </div>

      {/* Activity Scheduling Modal */}
      {showSchedulingModal && (
        <ActivitySchedulingModal
          activity={selectedActivity}
          schedulingData={schedulingData}
          setSchedulingData={setSchedulingData}
          onClose={() => setShowSchedulingModal(false)}
          onConfirm={(day, period) => {
            addActivityToTodo(
              selectedActivity.name,
              selectedActivity.category,
              selectedActivity.description,
              selectedActivity.mapsLink,
              day,
              period
            );
            setShowSchedulingModal(false);
          }}
          tripDuration={settings.endDate && settings.startDate ?
            Math.ceil((new Date(settings.endDate) - new Date(settings.startDate)) / (1000 * 60 * 60 * 24)) : 7}
        />
      )}
        </div>
    );
};

// Activity Scheduling Modal Component
function ActivitySchedulingModal({ activity, schedulingData, setSchedulingData, onClose, onConfirm, tripDuration }) {
    const { t } = useTranslation();
    const [selectedDay, setSelectedDay] = useState(schedulingData.day);
    const [selectedPeriod, setSelectedPeriod] = useState(schedulingData.period);

    // Get existing activities for validation
    const getExistingActivities = (day, period) => {
        const tripData = Storage.getTripData();
        const timelineKey = `day${day}_${period}`;
        return tripData.timeline?.[timelineKey] || [];
    };

    const canScheduleActivity = (day, period) => {
        const existingActivities = getExistingActivities(day, period);
        return existingActivities.length < 3;
    };

    const handleConfirm = () => {
        if (!canScheduleActivity(selectedDay, selectedPeriod)) {
            alert('Maximum 3 activities per period. Please choose a different day or period.');
            return;
        }
        onConfirm(selectedDay, selectedPeriod);
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
        }}>
            <div style={{
                background: 'white',
                borderRadius: '20px',
                padding: '30px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
            }}>
                <h2 style={{
                    color: '#8B7355',
                    fontSize: '24px',
                    fontWeight: '700',
                    margin: '0 0 20px 0',
                    textAlign: 'center'
                }}>{t('challengeModal.activityTitle')}</h2>

                <div style={{
                    background: '#f9f7f4',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '24px',
                    border: '2px solid #B8A082'
                }}>
                    <h3 style={{
                        color: '#8B7355',
                        fontSize: '18px',
                        fontWeight: '600',
                        margin: '0 0 8px 0'
                    }}>{activity?.name}</h3>
                    <p style={{
                        color: '#666',
                        fontSize: '14px',
                        margin: '0',
                        lineHeight: '1.4'
                    }}>{activity?.description}</p>
                </div>

                {/* Day Selection */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#8B7355',
                        marginBottom: '10px'
                    }}>{t('challengeModal.whichDay')}</label>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
                        gap: '8px'
                    }}>
                        {Array.from({ length: Math.min(tripDuration, 14) }, (_, i) => i + 1).map(day => (
                            <button
                                key={day}
                                onClick={() => setSelectedDay(day)}
                                style={{
                                    padding: '12px',
                                    border: '2px solid',
                                    borderColor: selectedDay === day ? '#5B8FA8' : '#ddd',
                                    borderRadius: '12px',
                                    backgroundColor: selectedDay === day ? '#5B8FA8' : 'white',
                                    color: selectedDay === day ? 'white' : '#8B7355',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    textAlign: 'center'
                                }}
                            >
                                {t('challengeModal.day', { number: day })}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Period Selection */}
                <div style={{ marginBottom: '30px' }}>
                    <label style={{
                        display: 'block',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#8B7355',
                        marginBottom: '10px'
                    }}>{t('challengeModal.whatTime')}</label>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '12px'
                    }}>
                        {[
                            { key: 'morning', label: t('challengeModal.morning'), desc: t('challengeModal.morningDesc') },
                            { key: 'afternoon', label: t('challengeModal.afternoon'), desc: t('challengeModal.afternoonDesc') }
                        ].map(period => {
                            const existingCount = getExistingActivities(selectedDay, period.key).length;
                            const isFull = existingCount >= 3;

                            return (
                                <button
                                    key={period.key}
                                    onClick={() => !isFull && setSelectedPeriod(period.key)}
                                    disabled={isFull}
                                    style={{
                                        padding: '16px',
                                        border: '2px solid',
                                        borderColor: selectedPeriod === period.key ? '#5B8FA8' : (isFull ? '#ff6b6b' : '#ddd'),
                                        borderRadius: '12px',
                                        backgroundColor: isFull ? '#ffe6e6' : (selectedPeriod === period.key ? '#5B8FA8' : 'white'),
                                        color: isFull ? '#ff6b6b' : (selectedPeriod === period.key ? 'white' : '#8B7355'),
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        cursor: isFull ? 'not-allowed' : 'pointer',
                                        textAlign: 'center',
                                        opacity: isFull ? 0.6 : 1
                                    }}
                                >
                                    <div>{period.label}</div>
                                    <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.8 }}>
                                        {isFull ? t('challengeModal.full', { count: existingCount }) : t('challengeModal.activities', { count: existingCount })}
                                    </div>
                                    <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.7 }}>
                                        {period.desc}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            color: '#8B7355',
                            border: '2px solid #8B7355',
                            padding: '12px 20px',
                            borderRadius: '25px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        {t('challengeModal.cancel')}
                    </button>

                    <button
                        onClick={handleConfirm}
                        style={{
                            background: '#C4714A',
                            color: 'white',
                            border: 'none',
                            padding: '12px 20px',
                            borderRadius: '25px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(184, 160, 130, 0.3)'
                        }}
                    >
                        {t('challengeModal.scheduleActivityButton')}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Plans;